import React, { createContext, useEffect, useCallback, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { withTranslation } from 'react-i18next';
import { message } from 'antd';
import { useCreateSubscriptionContext } from './createSubscriptionContext';
import { getAllVariableRelations, getAllVariables, saveVariable } from '../utils/services/variablesAPI';
import { paperSizes } from '../utils/complexPaperSizes';
import { getCreativeBlockVariableContract, getRepetitionContract, generatePDFPayload,
  updateStoreForNewFlow, updateStoreForBlock, autoTransformStandaloneBlocks, 
  getStoreStaticTableContents, getGroupsFromMap,
  removeWithChildren} from '../containers/ComplexDocumentDesign/complexDocumentHelper';
import { HEADER, FOOTER, TEXT_BLOCK, CREATIVE_BLOCK, FIXED_HEIGHT_BLOCKS, DYNAMIC_TABLE_BLOCK, STATIC_TABLE_ELEMENT,
  PAGE_NUMBER, STANDALONE_BLOCK, LINK_BLOCK, BUTTON_BLOCK, TABLE_OF_CONTENTS, 
  STATIC_TABLE_BLOCK, 
  CHECK_TYPE_KEY,
  READ_ONLY_KEY,
  IS_CHECKED_KEY,
  FULLY_DYNAMIC_FILLABLE_ELEMENTS,
  CREATIVE_BLOCK_ELEMENT_TYPES, 
  PERFECTDOC_RELATIONSHIP} from '../utils/complexDocumentConstants';
import { COMPLEX_PAGE_OPTIONS } from '../containers/ComplexDocumentDesign/translationValues';
import { DYNAMIC_TABLE_OPERATIONS } from '../containers/ComplexDocumentDesign/DynamicTableDesign/translationValues';
import { deepCopy, measurementConversion } from '../utils/javascript';
import ComplexDefaultStyleProvider, { useDefaultStyleContext,  }  from './complexDefaultStyleContext';
import { getBusinessLogicByType, reloadTableOfContents } from '../utils/helper';
import { getIndexedDBData, putIndexedDBData } from '../utils/indexedDBUtil';

export const COMPLEX_DOCUMENT_CONTEXT_KEYS = {
  ID:'documentId' ,
  FLOWS: 'flows',
  STYLES: 'style',
  DOCUMENT_NAME: 'documentName',
  PERSISTED_KEYS: 'backendIds',
  GROUP_ID: 'groupId',
  COMMUNICATION_ID: 'communicationId',
  RESOURCE_GROUP_ID: 'resourceGroupId',
  CATEGORY_ID: 'categoryId',
  LANGUAGE: 'language',
  DATA_SET: 'dataSet',
  TAGS: 'tags',
  CLONE_FLOW:"CLONE_FLOW",
  GLOBAL_VARIABLES: 'GLOBAL_VARIABLES',
  LOCAL_VARIABLES: 'LOCAL_VARIABLES',
  ADD_NEW_VARIABLE: 'ADD_NEW_VARIABLE',
  FETCHING_GLOBAL_VARIABLES: 'FETCHING_GLOBAL_VARIABLES',
  REFRENCED_FLOW_UUID:'REFRENCED_FLOW_UUID',
  REUSABLE_ASSERT_DATA:'REUSABLE_ASSERT_DATA',
  CREATE_BLOCK_COUNT: 'CREATE_BLOCK_COUNT',
  CLONE_BLOCK:'CLONE_BLOCK',
  CHART_ELEMENTS_COUNT: 'CHART_ELEMENTS_COUNT',
  REUSABLE_BLOCK_TYPE: 'REUSABLE_BLOCK_TYPE',
  IS_LANGUAGE_DETECTED: 'IS_LANGUAGE_DETECTED',
  IS_EMAIL_TEMPLATE: 'IS_EMAIL_TEMPLATE',
  AUTOSAVE_CALLBACK: 'AUTOSAVE_CALLBACK',
  GRID_LAYOUT_BLOCK: 'GRID_LAYOUT_BLOCK',
  SELECTED_FLOWS: 'SELECTED_FLOWS',
  VARIABLE_OVERRIDES: 'VARIABLE_OVERRIDES',
  NEW_LANGUAGE_CODE:"NEW_LANGUAGE_CODE",
  IS_DRAG_ACTION:'IS_DRAG_ACTION',
  TEMPLATE_INSERTS: 'TEMPLATE_INSERTS',
  IS_SAVING_CHANGES: 'IS_SAVING_CHANGES',
  DESIGN_SUMMARY: 'DESIGN_SUMMARY',
  DESIGN_SUMMARY_ID: 'DESIGN_SUMMARY_ID',
  NON_TEXT_VALIDATION: 'NON_TEXT_VALIDATION',
  IS_EDITABLE: 'IS_EDITABLE',
  INTERACTIVE_DOCUMENT_ID: 'INTERACTIVE_DOCUMENT_ID',
  DISPLAY_NAME: 'DISPLAY_NAME',
  RENDERING_TYPE: 'RENDERING_TYPE',
  IS_ACCESSIBLE: 'IS_ACCESSIBLE',
  GROUP_HIGHLIGHT_ID: 'GROUP_HIGHLIGHT_ID',
  ELEMENT_GROUPS: 'ELEMENT_GROUPS',
  FLOW_BUSINESS_LOGIC: 'FLOW_BUSINESS_LOGIC',
  IS_CLONED:'IS_CLONED',
  TEMPLATE_DESCRIPTION:'TEMPLATE_DESCRIPTION',
  RELATIONSHIPS: 'RELATIONSHIPS',
}

const MIN_COLUMN_WIDTH = 5;

const initialContext = () => ({
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]: '',
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: {},
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: {},
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.RESOURCE_GROUP_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.CATEGORY_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE]: '',
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_FLOW]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.ADD_NEW_VARIABLE]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: true,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.REFRENCED_FLOW_UUID]:[],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]:{},
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: 0,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: 0,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE]: 0,  
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_LANGUAGE_DETECTED]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.GRID_LAYOUT_BLOCK]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.SELECTED_FLOWS]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.VARIABLE_OVERRIDES]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: '',
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_SAVING_CHANGES]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.INTERACTIVE_DOCUMENT_ID]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME]: null,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.RENDERING_TYPE]: undefined,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE]: false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID]: null,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS] : [],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]:[],
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]:false,
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_DESCRIPTION]:'',
  [COMPLEX_DOCUMENT_CONTEXT_KEYS.RELATIONSHIPS]: [],
});

const DEFAULT_NEW_FLOW = (existingFlow = undefined, isEmail = false) => {
  const newPageUUID = existingFlow?.flowName === TABLE_OF_CONTENTS ? existingFlow?.uuid|| uuidv4() : uuidv4();
  const paperSize = existingFlow && existingFlow?.paperSize ? existingFlow.paperSize : (existingFlow?.size || (isEmail ? 'custom' : 'a4'));
  const paperOrientation = existingFlow && existingFlow?.paperOrientation ? existingFlow.paperOrientation : (existingFlow?.orientation || 'portrait');
  const backgroundColor = existingFlow && existingFlow.style ? (existingFlow.style.backgroundColor || (isEmail ? '#f5f8fa' : '#FFFFFF')) : (existingFlow?.backgroundColor || (isEmail ? 'f5f8fa' : '#FFFFFF'));
  const contentColor = isEmail ? existingFlow && existingFlow.style ? (existingFlow.style.color || '#FFFFFF') : (existingFlow?.color || '#FFFFFF') : undefined;
  const ppi = existingFlow?.style?.ppi;
  const margin = existingFlow && existingFlow.style && existingFlow.style.padding ? [
    existingFlow.style.padding.top,
    existingFlow.style.padding.right,
    existingFlow.style.padding.bottom,
    existingFlow.style.padding.left
  ] : (existingFlow?.margin ? existingFlow.margin : [0, 0, 0, 0]);
  let paperDimensions; 
  if(paperSize === 'custom') {
    paperDimensions = {
      widthInPX: existingFlow.width || existingFlow?.style.width,
      heightInPX: existingFlow.height || existingFlow?.style.height,
      widthInMM: existingFlow.widthInMM || measurementConversion(existingFlow?.style.width, 'mm'),
      heightInMM: existingFlow.heightInMM || measurementConversion(existingFlow?.style.height, 'mm'),
    }
  } else {
    paperDimensions = paperSizes.filter(paperMeta => paperMeta.lowercaseName === paperSize)[0][paperOrientation];
  }
  const fontFamily = existingFlow && (existingFlow.style?.fontFamily);
  const fontSize = existingFlow && (existingFlow.style?.fontSize);
  return ({
    uuid: newPageUUID,
    flowName:existingFlow?.flowName,
    margin: margin,
    size: paperSize,
    width: paperDimensions.widthInPX,
    height: paperDimensions.heightInPX,
    widthInMM: paperDimensions.widthInMM,
    heightInMM: paperDimensions.heightInMM,
    orientation: paperOrientation,
    backgroundColor: backgroundColor,
    color: contentColor,
    fontFamily,
    fontSize,
    ppi: ppi,
    isStandalone: existingFlow.isStandalone,
    pages: [`${newPageUUID}-0`],
    [`${newPageUUID}-0`]: []
  });
}

const DEFAULT_CREATIVE_BLOCK = (uuid, isChild = false, parentBlockUUID = undefined) => {
  return ({
    uuid,
    style: {
      height: 100,
    },
    variableContract: [],
    contents: [],
    type: 'creative',
    isChild,
    parentBlockUUID,
    blockType: 'creative',
  });
}

const DEFAULT_TEXT_CONTENTS = (fontFamily, fontSize) => (
  new Array(
  {
    contentType: 'PARAGRAPH',
    style: {
      lineSpacing: 1.5,
      fontWeight: [],
      color: '#000000',
      fontFamily,
      fontSize
    },
    content: ' '
  },
  {
    contentType: "LINE_BREAK",
    content: "\n",
    style: {
      lineSpacing: 1,
      alignment: "center",
      color: "#000000",
      fontFamily,
      fontSize
    }
  })
);

const ComplexDocumentContext = createContext(null);

export const useDocumentContext = (selector, customName = '') => {
  const store = useContext(ComplexDocumentContext);
  if (!store) {
    throw new Error('Store not found');
  }

  const [ state, setState ] = useState(selector(store.get(), `initialState-${customName}`));

  useEffect(() => {
    return store.subscribe(() => {
      setState(previousState => {
        return selector(store.get(), previousState);
      });
    });
  }, [selector]);

  return [state, store];
}

const HISTORY_INITIAL_STATE = () => ({
  history: [],
  undoStack: [],
  redoStack: [],
  refreshStack: false,
  applyingChanges: false
})

const ComplexDocumentHistoryContext = createContext(HISTORY_INITIAL_STATE());

export const useComplexDocumentHistory = (selector, customSetter = undefined) => {
  const store = useContext(ComplexDocumentHistoryContext);
  if (!store) {
    throw new Error('Store not found');
  }

  const [ state, setState ] = useState(selector(store.get()));

  useEffect(() => {
    return store.subscribe(() => {
      setState(previousState => {
        return selector(store.get(), previousState);
      });
    }, 'history');
  }, []);

  return [state, store];
}

const Provider = ({ children, dataSet, t, masterVariables, customState = {}, isOpenDesign = false }) => {
  const store = useCreateSubscriptionContext(Object.assign({}, initialContext(), { dataSet }, customState), undefined, false);
  const [, historyStore] = useComplexDocumentHistory(() => null);
  const [documentStyle, defautlStyleStore] = useDefaultStyleContext(store=>({fontFamily: store.fontFamily, fontSize: store.fontSize}));

  store.addNewFlow = useCallback((uuid, existingFlow, isUserAction = true) => {   
    const currentStore = store.get();
    if (uuid === undefined) {
      uuid =
        currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][
          currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].length - 1
        ]; // Last page
    }
    const flowNumber = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].findIndex(
      flow => flow === uuid,
    );
    const existingFlowObj = flowNumber !== -1 &&
      deepCopy(currentStore[currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][flowNumber]]);
    let newFlow;
    if (existingFlow) {
      newFlow = existingFlow;
    } else {
      if(existingFlowObj.flowName === TABLE_OF_CONTENTS){existingFlowObj.flowName = ''};
      newFlow = DEFAULT_NEW_FLOW(existingFlowObj, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]);
      newFlow.isStandalone = false;
      newFlow.flowName= '';
    }
    const newFlows = [
      ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(0, flowNumber + 1),
      newFlow.uuid,
      ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(flowNumber + 1),
    ];
    const storeObj = { [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: newFlows, [newFlow.uuid]: newFlow };
    let pageNumber;
    if (existingFlowObj?.pageNumber) {
      newFlow.pageNumber = uuidv4();
      pageNumber = { ...currentStore[existingFlowObj?.pageNumber], uuid: newFlow.pageNumber };
      storeObj[pageNumber.uuid] = pageNumber;
    }

    store.set(storeObj);
    if (isUserAction) {
      historyStore.set({
        changes: { entity: 'flow', operation: 'add', data: { uuid: uuid, flow: newFlow } },
      });
    }
    return newFlow.uuid;
  }, []);

  store.deleteFlow = useCallback((uuid, isUserAction = true) => {
    const currentStore = store.get();
    const flowNumber = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].findIndex(page => page === uuid);
    const deleteFlow = currentStore[uuid];
    const newFlows = [
      ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(0, flowNumber),
      ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(flowNumber + 1)
    ];
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: newFlows }, [uuid]);
    if (isUserAction) {
      historyStore.set({ changes: { entity: 'flow', operation: 'delete', data: {
        uuid: uuid,
        flow: deleteFlow,
        deleteId: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][uuid],
        before: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][flowNumber - 1] || 0
      } } });
    }
  }, []);

  store.swapFlows = useCallback((uuid, details, isUserAction = true) => {
    const currentStore = store.get();
    const { currentPosition, targetPosition } = details;
    console.log("SwapFlows :: ", details);
    const newFlows = [...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]];
    const temp = newFlows[targetPosition];
    newFlows[targetPosition] = newFlows[currentPosition];
    newFlows[currentPosition] = temp;
    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: newFlows });
    if (isUserAction) {
      historyStore.set({ changes: { entity: 'flow', operation: 'move', data: {
        uuid: uuid,
        flow: { uuid },
        before: currentPosition,
        after: targetPosition
      }}});
    }
  }, []);

  store.removeTextFromParentPage = useCallback((pageUUID, pageId, itemUUID, overflowPageId) => {
    const pageDetails = store.get()[pageUUID];
    console.log("Moving :: ", pageDetails[overflowPageId].slice(1));
    store.set({
      [pageUUID]: Object.assign({}, pageDetails, {
        [pageId]: pageDetails[pageId].filter(item => item !== itemUUID),
        [overflowPageId]: [itemUUID, ...pageDetails[overflowPageId]]
      })
    });
    console.log("Changes in store :: ", store.get());
  }, []);

  store.moveRowsToOverflowingTable = useCallback((itemUUID, overflowTable, rowIndex, overflowPageId, flowUUID, updateMainTable = true) => {
    console.log("moving rows from original table to overflow table :: ", overflowTable.uuid, rowIndex);
    const flowDetails = store.get()[flowUUID];
    const mainTable = store.get()[itemUUID];
    let extraChanges = {};
    if (updateMainTable) {
      extraChanges = {
        [itemUUID]: Object.assign({}, mainTable, {
          overflowingDataLength: rowIndex
        }),
      }
    }
    const overflowingRowIndex = rowIndex + (mainTable.overflowingDataIndex || 0);
    store.set({
        [overflowTable.uuid]: Object.assign({}, overflowTable, {
          overflowingTable: true,
          overflowingDataIndex: overflowingRowIndex
        }),
        [flowUUID]: Object.assign({}, flowDetails, {
          [overflowPageId]: [overflowTable.uuid, ...flowDetails[overflowPageId]]
        }),
        ...extraChanges
      });
    console.log("Changes in store :: ", store.get());
  }, []);

  store.removeTableFromParentPage = useCallback((pageUUID, pageId, itemUUID, overflowPageId, overflowTable) => {
    const pageDetails = store.get()[pageUUID];
    console.log("Moving :: ", pageDetails[overflowPageId].slice(1));
    store.set({
      [pageUUID]: Object.assign({}, pageDetails, {
        [pageId]: pageDetails[pageId].filter(item => item !== itemUUID),
        [overflowPageId]: [overflowTable.uuid, ...pageDetails[overflowPageId].slice(1)]
      }),
      [overflowTable.uuid]: Object.assign({}, overflowTable, {
        overflowingTable: false,
        overflowingDataIndex: undefined
      })
    });
    console.log("Changes in store :: ", store.get());
  }, []);

  store.overflowPage = useCallback((pageUUID, pageId, breakOffIndex, maxHeight = undefined, allChildBlocks = [], actualSize = undefined) => {
    const pageDetails = store.get()[pageUUID];
    const existingPages = pageDetails.pages;
    const isFirstPage = pageId.endsWith('-0');
    const existingPageIndex = parseInt(pageId.replaceAll(`${pageUUID}-`, ''), 10);
    const overflowPageId = `${pageUUID}-${existingPageIndex + 1}`;
    let repeatBlock = undefined;
    // if (existingPages.length - 1 === pageId) { // Introduce a new page
    // TODO : Identify the type of block and perform separation accordingly
    const intersectingBlock = store.get()[pageDetails[pageId][breakOffIndex]];
    console.log({ intersectingBlock });
    if (intersectingBlock?.blockType === TEXT_BLOCK) {
      repeatBlock = Object.assign({}, intersectingBlock, {
        uuid: uuidv4(),
        contents: []
      });
    } else if (intersectingBlock?.blockType === DYNAMIC_TABLE_BLOCK) {
      repeatBlock = Object.assign({}, intersectingBlock, {
        uuid: uuidv4(),
        contents: intersectingBlock.contents.map(content => Object.assign({}, content, {
          uuid: uuidv4()
        })),
        overflowingTable: true,
        overflowingDataIndex: undefined
      });
    }

      // TODO : Create a new block with split details from existing block
      const firstSplitOfBlocks = pageDetails[pageId].slice(0, breakOffIndex + (repeatBlock ? 1 : 0));
      const secondSplitOfBlocks = repeatBlock ? pageDetails[pageId].slice(breakOffIndex + 1) : pageDetails[pageId].slice(breakOffIndex);
      const changes = pageDetails[overflowPageId] ? {} : {
         pages: [...existingPages, overflowPageId]
      }
      if (isFirstPage && pageDetails.allBlockHeights === undefined) {
        changes.allBlockHeights = allChildBlocks.reduce((acc, child) => ({ ...acc, [child.id]: child.offsetHeight }), {});
        const numberOfPages = Math.floor(actualSize / maxHeight);
        if (existingPages.length === 1) {
          changes.pages = [pageId];
          (new Array(numberOfPages).fill(0)).forEach((pageNumber, pageIndex) => {
            const overflowPage = `${pageUUID}-${pageIndex + 1}`;
            changes.pages.push(overflowPage);
            changes[overflowPage] = [];
          });
        }
      }

      console.log({ repeatBlock, firstSplitOfBlocks, secondSplitOfBlocks, changes });
      // console.log({ breakOffIndex, firstSplitOfBlocks, secondSplitOfBlocks, overflowPageId });
      store.set({
        [pageUUID]: Object.assign({}, pageDetails, {
          [pageId]: firstSplitOfBlocks
        })
      });

      let overflowingPageContents = [...secondSplitOfBlocks, ...(pageDetails[overflowPageId] || [])];
      if (maxHeight !== undefined && secondSplitOfBlocks.length > 0 && overflowingPageContents.length > 0) {
        let heightOfContents = 0;
        changes[`limit-${overflowPageId}`] = overflowingPageContents.findIndex(blockUUID => {
          heightOfContents += pageDetails.allBlockHeights ? pageDetails.allBlockHeights[blockUUID] : changes.allBlockHeights[blockUUID];
          if (heightOfContents > maxHeight) {
            return true;
          }
          return false;
        });
        if (changes[`limit-${overflowPageId}`] === -1) {
          changes[`limit-${overflowPageId}`] = undefined;
        }
      }
      changes[`limit-${pageId}`] = undefined;

      store.set({
        [pageUUID]: Object.assign({}, pageDetails, {
          ...changes,
          [pageId]: firstSplitOfBlocks,
          [overflowPageId]: overflowingPageContents
        })
      });
      console.log("Change in store overflowPage 2 :: ", JSON.parse(JSON.stringify(store.get())));

      const quillDOM = document.getElementById(`q-container-${intersectingBlock.uuid}`);
      const tableDOM = document.getElementById(`dynamic-table-${intersectingBlock.uuid}`);
      if (quillDOM) {
        quillDOM.dispatchEvent(new CustomEvent('textOverflow', { detail: { overflowBlock: repeatBlock, pageId, overflowPageId } }));
      } else if (tableDOM) {
        tableDOM.dispatchEvent(new CustomEvent('tableOverflow', { detail: { overflowBlock: repeatBlock, pageId, overflowPageId } }));
      }
  }, []);

  store.onPageHeightExpand = useCallback((pageUUID, pageId) => {
    const pageDetails = store.get()[pageUUID];
    const existingPages = pageDetails.pages;
    const existingPageIndex = parseInt(pageId.replaceAll(`${pageUUID}-`, ''), 10);
    let newPageDetails = Object.assign({}, pageDetails);
    if (existingPageIndex === existingPages.length - 1) {
      if (pageDetails[pageId].length) return; // Changes in the last page of the flow
      // Removing page with no content
      newPageDetails.pages = existingPages.filter(existingPageId => existingPageId !== pageId);
      delete newPageDetails[pageId];
      store.set({
        [pageUUID]: newPageDetails
      });
      return;
    }
    let deleteKeys = [];
    for(let i = existingPageIndex + 1; i < existingPages.length; i++) {
      for (let nextBlock of newPageDetails[`${pageUUID}-${i}`]) {
        if (newPageDetails[pageId].includes(`${pageUUID}-${i}`)) {
          continue;
        }
        newPageDetails[pageId].push(nextBlock);
      }
      deleteKeys.push(`${pageUUID}-${i}`);
    }
    deleteKeys.forEach(key => {
      delete newPageDetails[key];
    });
    newPageDetails.pages = newPageDetails.pages.filter(existingPageId => !deleteKeys.includes(existingPageId));
    store.set({
      [pageUUID]: newPageDetails
    })
    console.log("Change in store shrinkPage :: ", store.get());
  }, []);

  store.checkIfAllBlocksFitContentArea = useCallback((flowUUID, newContentAreaHeight, newStore = undefined) => {
    const flow = store.get()[flowUUID];
    // Check if all blocks within the page is lesser than the new content area
    const allBlocks = Object.keys(flow).filter(key => key.startsWith(`${flowUUID}-`)).reduce((acc, pageKey) => {
      return [...acc, ...flow[pageKey]];
    }, []);
    const blocksThatDoNotFit = allBlocks.filter(blockUUID => {
      const blockDetails = newStore ? newStore[blockUUID] : store.get()[blockUUID];
      if (FIXED_HEIGHT_BLOCKS.includes(blockDetails.type) && blockDetails.style.height) {
        return blockDetails.style.height > newContentAreaHeight;
      }
    });
    return blocksThatDoNotFit;
  }, []);

  store.updateBlock = useCallback((blockOperation, blockUUID, details, isUserAction = true) => {
    const allVariables = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES];
    const blockDetails = store.get()[blockUUID];
    const flow = store.get()[details.flowUUID];
    const element_groups = store.get()['ELEMENT_GROUPS'];
    console.log("Coming here updateBlock ", blockOperation, blockUUID, details);
    switch(blockOperation) {
      case 'name':
        let previousName = blockDetails.blockName;
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              blockName: details.name
            })
          });
        let entityType = blockDetails.type === "header" ? "header" : "footer" ? "footer" : "block";
        if (isUserAction) {
          historyStore.set({ changes: { entity: entityType, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            block: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: previousName,
            after: details.name
          }}});
        }
        return;
      case 'tableProperties': // TODO : Change variable contract according to properties
        let previousProperties = blockDetails.style.tableProperties;
        const newTableProperties = Object.assign({}, blockDetails.style.tableProperties, details.tableProperties);
        if (!newTableProperties.showHeader && !newTableProperties.showGroupHeaderContent) {
          newTableProperties.showHeaderOnAllPages = false;
        }
        const childBlocks = [];
        if (newTableProperties.showTopBlock) {
          childBlocks.push(store.get()[newTableProperties.topBlock]);
        }
        if (newTableProperties.showBottomBlock) {
          childBlocks.push(store.get()[newTableProperties.bottomBlock]);
        }
        const { variables } = getCreativeBlockVariableContract(
          blockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
          allVariables,
          blockDetails?.style?.summary,
          false,
          blockDetails.variableContract[0].name,
          childBlocks,
          newTableProperties,
          blockDetails
        );
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            style: Object.assign({}, blockDetails.style, {
              tableProperties: newTableProperties
            }),
            variableContract: variables
          })
        });
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            block: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: previousProperties,
            after: details.tableProperties
          }}});
        }
        return;
      case 'repetitive':
        let isAlreadyRepetitive = blockDetails.variableContract?.length > 0 ? blockDetails.variableContract[0].type === 'object' : false
        let createBlocksCount = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT];
        let repeat = details.repeat, variableName = details.variableName || `repeat-${createBlocksCount}`;
        let contentsDetails = details?.contents;
        let isTableName = details.isTableName;
        if (details.op && details['repetitive']) {
          repeat = details['repetitive'].repeat;
          variableName = details['repetitive'].variableName;
          contentsDetails = details['repetitive'].contents;
          isTableName = details['repetitive'].isTableName;
        }
        if (blockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
          isAlreadyRepetitive = isTableName ? true : blockDetails.variableContract[0].isArray || false;
        }
        if (!details.variableName) {
          createBlocksCount++;
        }
        const previousRepetition = {
          repeat: isAlreadyRepetitive,
          variableName: isAlreadyRepetitive
            ? isTableName && blockDetails.variableContract[0].isArray
              ? blockDetails.variableContract[0].content.find(item => item.type === 'object')?.name
              : blockDetails.variableContract[0].name
            : '',
          contents: contentsDetails ? blockDetails?.contents : undefined,
          isTableName: details.isTableName
        };
        const storeObj = {
          [blockUUID]: Object.assign({}, blockDetails, {
            variableContract: getRepetitionContract(blockDetails.variableContract, repeat, variableName, blockDetails.blockType === DYNAMIC_TABLE_BLOCK, isTableName)
          }),
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: createBlocksCount
        };
        if (contentsDetails) {
          storeObj[blockUUID].contents = contentsDetails;
          const { variables } = getCreativeBlockVariableContract(
            contentsDetails,
            allVariables,
            blockDetails?.style?.summary,
            repeat,
            variableName,
          );
          storeObj[blockUUID].variableContract = variables;
        }
        store.set(storeObj);
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            block: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: previousRepetition,
            after: {
              repeat: details.repeat,
              variableName: variableName,
              contents: contentsDetails,
              isTableName: details.isTableName
            }
          }}});
        }
        return;
      case 'html': {
        const previousVariableName = blockDetails.variableContract[0].name;
        const newVariableContract = deepCopy(blockDetails.variableContract);
        let variableName = details.variableName;
        if (details.op && details?.['html']?.variableName) {
          variableName = details?.['html']?.variableName;
        }
        newVariableContract[0].name = variableName;
        const storeObj = {
          [blockUUID]: Object.assign({}, blockDetails, {
            variableContract: newVariableContract
          }),
        };
        store.set(storeObj);
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            block: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: {
              variableName: previousVariableName,
            },
            after: {
              variableName: details.variableName
            }
          }}});
        }
        return;
      }
      case 'deleteChildBlock':
        if (blockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
          let childBlockType;
          if (blockDetails.style.tableProperties.topBlock === details.block.uuid) {
            childBlockType = 'topBlock';
          } else if (blockDetails.style.tableProperties.bottomBlock === details.block.uuid) {
            childBlockType = 'bottomBlock';
          }
          if (childBlockType) {
            store.updateBlock('table-operations', blockDetails.uuid, {
              'table-operations': {
                op: 'delete',
                entity: childBlockType,
                childBlock: true
              },
              itemUUID: blockDetails.uuid,
              flowUUID: details.flowUUID,
            });
          }
        }
        return;
      case 'table-operations':
        let action = details['table-operations'];
        const otherChildBlocks = [];
        if (blockDetails.style.tableProperties.showTopBlock) {
          otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.topBlock]);
        }
        if (blockDetails.style.tableProperties.showBottomBlock) {
          otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.bottomBlock]);
        }
        if (action.entity === 'column') {
          const previousColumns = JSON.parse(JSON.stringify(blockDetails.contents));
          if (action.op === 'add') {
            let newColumn = action.newColumn;
            if (newColumn === undefined) {
              newColumn = JSON.parse(JSON.stringify(blockDetails.contents[action.headerIndex]));
              newColumn.uuid = uuidv4();
              newColumn.headerContent.contents[0].content = `Header ${blockDetails.contents.length + 1}`;
              const anyGroupContent = blockDetails.contents.find(content => content.groupHeaderContent);
              if (anyGroupContent) {
                newColumn.groupHeaderContent = Object.assign({}, anyGroupContent.groupHeaderContent, {
                  columnSpan: 1,
                  contents: [...newColumn.headerContent.contents]
                });
              }
              const fontStyles = newColumn.content.contents.length > 0 ? {
                fontFamily: newColumn.content.contents[0].style?.fontFamily,
                fontSize: newColumn.content.contents[0].style?.fontSize,
              } : undefined;
              newColumn.content.contents = DEFAULT_TEXT_CONTENTS(fontStyles?.fontFamily, fontStyles?.fontSize);
            }
            const position = action.isLeft === false ? action.headerIndex + 1 : action.headerIndex;

            let newHeaders = blockDetails.contents.toSpliced(position, 0, newColumn);
            const overallWidth = newHeaders.reduce((acc, header) => {
              return acc + header.headerContent.style.width;
            }, 0);
              let extraWidth = overallWidth - 100;
              let delta = action.isLeft === true ? 1 : -1;
              const resizableHeaders = newHeaders.filter((header, index) => {
                return header.headerContent.style.width - (extraWidth / newHeaders.length) > 0;
              }).map(header => header.uuid);
              newHeaders = newHeaders.map((header, index) => {
                  return Object.assign({}, header, {
                    headerContent: Object.assign({}, header.headerContent, {
                      style: Object.assign({}, header.headerContent.style, {
                        width:  header.headerContent.style.width -
                          (resizableHeaders.includes(header.uuid) ? (extraWidth / resizableHeaders.length) : 0)
                      })
                    })
                  });
              })

            const { variables } = getCreativeBlockVariableContract(
              newHeaders.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );

            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newHeaders,
                variableContract: variables
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: 'column',
                  contents: previousColumns
                },
                after: {
                  op: 'replace',
                  entity: 'column',
                  contents: newHeaders
                }
              }}});
            }
          } else if (action.op === 'replace') {

            const newHeaders = action.contents;

            const { variables } = getCreativeBlockVariableContract(
              newHeaders.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newHeaders,
                variableContract: variables
              })
            });

            if (isUserAction) {
              // TODO : Add to history
            }
          } else if (action.op === 'delete') {
            const previousHeaders = JSON.parse(JSON.stringify(blockDetails.contents));
            let deleteColumn = blockDetails.contents[action.headerIndex];
            let newHeaders = removeWithChildren(blockDetails.contents, action.headerIndex);

            let leftIndex = action.headerIndex - 1;
            if (action.headerIndex === 0) {
              leftIndex = 0;
            }
            newHeaders[leftIndex].headerContent.style.width += deleteColumn.headerContent.style.width;
            if (newHeaders[leftIndex].groupHeaderContent?.columnSpan) {
              newHeaders[leftIndex].groupHeaderContent.columnSpan = newHeaders[leftIndex].groupHeaderContent?.columnSpan === 1
                ? 1
                : newHeaders[leftIndex].groupHeaderContent?.columnSpan - 1;
            }

            const { variables } = getCreativeBlockVariableContract(
              newHeaders.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newHeaders,
                variableContract: variables
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: 'column',
                  contents: previousHeaders
                },
                after: {
                  op: 'replace',
                  entity: 'column',
                  contents: newHeaders
                }
              }}});
            }
          } else if (action.op === 'rearrange') {
            const newColumns = JSON.parse(JSON.stringify(blockDetails.contents));

            const targetIndex = isUserAction && action.dropIndex < action.dragIndex ? action.dropIndex + 1 : action.dropIndex;
            console.log({ source: action.dragIndex, target: targetIndex, original: action.dropIndex });

            if (action.dragIndex === targetIndex) return;

            newColumns.splice(targetIndex, 0, newColumns.splice(action.dragIndex, 1)[0]);

            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newColumns
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  dragIndex: targetIndex,
                  dropIndex: action.dragIndex
                },
                after: {
                  ...action,
                  dropIndex: targetIndex
                }
              }}});
            }
          } else if (action.op === 'resize') {
            const previousColumns = JSON.parse(JSON.stringify(blockDetails.contents));
            const newColumns = JSON.parse(JSON.stringify(blockDetails.contents));
            // Checking if column width is accommodated within current flow dimensions and padding of the same block
            const fullWidth = flow.width - flow.margin[1] - flow.margin[3] - blockDetails.style.padding?.left - blockDetails.style.padding?.right;

            const differenceInWidth = (action.differenceInWidth / fullWidth) * 100;

            newColumns[action.columnIndex].headerContent.style.width += differenceInWidth;
            newColumns[action.columnIndex].content.style.width += differenceInWidth;

            if (newColumns[action.columnIndex].headerContent.style.width <= MIN_COLUMN_WIDTH) {
              message.warn(t(DYNAMIC_TABLE_OPERATIONS.CANNOT_MINIMIZE_COLUMN.key, DYNAMIC_TABLE_OPERATIONS.CANNOT_MINIMIZE_COLUMN.value));
              return;
            }

            let tableWidth = newColumns.reduce((acc, col) => acc + col.headerContent.style.width, 0);

              // Checking if change in width can be adjusted in the next column's width
              if (newColumns[action.columnIndex + 1] !== undefined) {
                const nextColumnWidth = newColumns[action.columnIndex + 1].headerContent.style.width - differenceInWidth;
                if (nextColumnWidth <= MIN_COLUMN_WIDTH) {
                  message.warn(t(DYNAMIC_TABLE_OPERATIONS.SOME_COLUMNS_ARE_TOO_SMALL_ON_RESIZE.key, DYNAMIC_TABLE_OPERATIONS.SOME_COLUMNS_ARE_TOO_SMALL_ON_RESIZE.value));
                  return;
                } else {
                  newColumns[action.columnIndex + 1].headerContent.style.width = nextColumnWidth;
                  newColumns[action.columnIndex + 1].content.style.width = nextColumnWidth;

                  tableWidth = newColumns.reduce((acc, col) => acc + col.headerContent.style.width, 0);
                  // console.log({ tableWidth, pageWidth }, "2")

                  if (tableWidth > 100) {
                    message.warn(t(DYNAMIC_TABLE_OPERATIONS.UNABLE_TO_RESIZE_COLUMN.key, DYNAMIC_TABLE_OPERATIONS.UNABLE_TO_RESIZE_COLUMN.value));
                    return;
                  }
                }
              } else {
                console.log("Should not come here ideally since last column resize is disabled in design");
                message.warn(t(DYNAMIC_TABLE_OPERATIONS.UNABLE_TO_RESIZE_COLUMN.key, DYNAMIC_TABLE_OPERATIONS.UNABLE_TO_RESIZE_COLUMN.value));
                return;
              }

            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newColumns
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: action.entity,
                  contents: previousColumns
                },
                after: {
                  op: 'replace',
                  entity: action.entity,
                  contents: newColumns
                }
              }}});
            }
          } else if (action.op === 'mapVariable') {
            let newColumns = JSON.parse(JSON.stringify(blockDetails.contents));
            let previousContent;
            let columnStyling = newColumns[0].content?.style;
            let textStyling = newColumns[0].content?.contents[0]?.style;
            let newLineStyling = newColumns[0].content?.contents[1]?.style;

            newColumns = newColumns.map((col, colIndex) => {
              if (col.uuid === action.headerUUID) {
                previousContent = col.content;
                return Object.assign({}, col, {
                  content: Object.assign({}, col.content, {
                    contentType: action.content.contentType || col.content.contentType,
                    style: action.content.style ? Object.assign({}, col.content.style, columnStyling || {}, action.content.style) : col.content.style,
                    contents: action.content.contents ? action.content.contents.map((textContent, textIndex) => {
                      if (textIndex === 0 && textStyling) {
                        return Object.assign({}, textContent, {
                          style: Object.assign({}, textContent.style, textStyling)
                        });
                      } else if (textIndex === 1 && newLineStyling) {
                        return Object.assign({}, textContent, {
                          style: newLineStyling
                        });
                      }
                      return textContent;
                    }) : col.content.contents
                  })
                })
              } else {
                if (columnStyling === undefined) {
                  columnStyling = col.content.style;
                }
                if (textStyling === undefined) {
                  textStyling = col.content?.contents[0]?.style;
                }
                if (newLineStyling === undefined) {
                  newLineStyling = col.content?.contents[1]?.style;
                }
              }
              return col;
            });

            const { variables } = getCreativeBlockVariableContract(
              newColumns.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );

            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newColumns,
                variableContract: variables
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  content: previousContent,
                  headerUUID: action.headerUUID
                },
                after: {
                  ...action
                }
              }}});
            }
          } else if (action.op === 'repeat') {
            let newColumns = JSON.parse(JSON.stringify(blockDetails.contents));
            let previousContent;

            const tableName = blockDetails.variableContract[0].isArray
              ? blockDetails.variableContract.find(v => v.type === 'object').name
              : blockDetails.variableContract[0].name;

            newColumns = newColumns.map((col, colIndex) => {
              if (col.uuid === action.columnUUID) {
                previousContent = col.repeatVariable || undefined;
                return Object.assign({}, col, {
                  repeatVariable: action.content?.replace(`${tableName}.`, '')
                })
              }
              return col;
            });

            console.log("newColumns :: ", newColumns, action);

            const { variables } = getCreativeBlockVariableContract(
              newColumns.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );

            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newColumns,
                variableContract: variables
              })
            });

            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  content: previousContent,
                  columnUUID: action.columnUUID
                },
                after: {
                  ...action
                }
              }}});
            }
          } else if (action.op === 'styles') {
            const previousStyle = Object.assign({}, blockDetails.contents[0].headerContent.style, {
              backgroundColor: blockDetails.contents[0].headerContent.style.backgroundColor || 'transparent'
            });
            let newStyle = action.style;
            if (action.key === 'border') {
              newStyle = {
                border: Object.assign({}, previousStyle.border, action.style.border)
              };
            }
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: blockDetails.contents.map(col => Object.assign({}, col, {
                  headerContent: Object.assign({}, col.headerContent, {
                    style: Object.assign({}, col.headerContent.style, newStyle)
                  }),
                  groupHeaderContent: col.groupHeaderContent ? Object.assign({}, col.groupHeaderContent, {
                    style: Object.assign({}, col.groupHeaderContent.style, newStyle)
                  }) : col.groupHeaderContent
                }))
              })
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  style: previousStyle
                },
                after: {
                  ...action
                }
              }}});
            }
            console.log("Changes in table store :: ", store.get());
          }
        } else if (action.entity === 'cell') {
          console.log("table operations :: ", details); 
          if (action.op === 'formatting') {
            const { columnUUID, contents: actionContents } = action;
            const isRedo = !details.op || details.op === 'redo';

            const getPreviousContent = () =>
              columnUUID
                ? blockDetails.contents.find(item => item.uuid === columnUUID)?.content?.contents
                : blockDetails.contents;

            const getUpdatedContents = (col, index) => {
              const colContents = col?.content?.contents || [];

              if (isRedo || columnUUID) {
                return colContents.length
                  ? actionContents.map((textContent, i) => ({
                      ...textContent,
                      content: i === 0 ? colContents[0]?.content : textContent.content,
                    }))
                  : [];
              }

              return colContents.length ? actionContents[index]?.content?.contents || [] : [];
            };

            const previousContent = getPreviousContent();

            const updatedContents = blockDetails.contents.map((col, index) => {
              if (!columnUUID || col.uuid === columnUUID) {
                return {
                  ...col,
                  content: {
                    ...col.content,
                    contents: getUpdatedContents(col, index),
                  },
                };
              }
              return col;
            });

            store.set({
              [blockUUID]: {
                ...blockDetails,
                contents: updatedContents,
              },
            });
            console.log("Changes in table store :: ", store.get());
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  columnUUID: action.columnUUID,
                  contents: previousContent
                },
                after: {
                  ...action
                }
              }}});
            }
          } else if (action.op === 'styles') {
            let {
              columnUUID,
              style: newStyle,
              headerStyle: newHeaderStyle,
              tableStyle: newTableStyle,
              key,
            } = action;
            
            let previousStyle, previousHeaderStyle, previousTableStyle;
            
            const targetCol = columnUUID
              ? blockDetails.contents.find(item => item.uuid === columnUUID)
              : blockDetails.contents;
            
            if (columnUUID) {
              const { style: targetColStyle = {} } = targetCol.content || {};
              const { style: targetHeaderStyle = {} } = targetCol.headerContent || {};
            
              previousStyle = {
                ...targetColStyle,
                backgroundColor: targetColStyle.backgroundColor ?? 'transparent',
              };
            
              if (newHeaderStyle) {
                previousHeaderStyle = { ...targetHeaderStyle };
              }
            
              if (newTableStyle) {
                previousTableStyle = { ...blockDetails.style };
              }
            } else {
              previousStyle = targetCol;
            }
            
            const applyStyle = (existingStyle = {}, newStyleObj = {}) => {
              return key === 'border'
                ? { ...existingStyle, border: { ...existingStyle.border, ...newStyleObj.border } }
                : { ...existingStyle, ...newStyleObj };
            };
            
            const updatedContents = blockDetails.contents.map((col, index) => {
              const isTargetColumn = !columnUUID || col.uuid === columnUUID;
              const isDetails_redo = !details.op || details.op === 'redo';
            
              if (!isTargetColumn) return col;
            
              if (!isDetails_redo && !columnUUID) {
                return newStyle[index]; 
              }
            
              return {
                ...col,
                headerContent: newHeaderStyle
                  ? {
                      ...col.headerContent,
                      style: applyStyle(col.headerContent?.style, newHeaderStyle),
                    }
                  : col.headerContent,
                groupHeaderContent: newHeaderStyle && col.groupHeaderContent
                  ? {
                      ...col.groupHeaderContent,
                      style: applyStyle(col.groupHeaderContent?.style, newHeaderStyle),
                    }
                  : col.groupHeaderContent,
                content: {
                  ...col.content,
                  style: applyStyle(col.content?.style, newStyle),
                },
              };
            });
            
            store.set({
              [blockUUID]: {
                ...blockDetails,
                style: newTableStyle
                  ? applyStyle(blockDetails.style, newTableStyle)
                  : blockDetails.style,
                contents: updatedContents,
              },
            });
            
          
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: action.op,
                  entity: action.entity,
                  columnUUID: action.columnUUID,
                  style: previousStyle,
                  headerStyle: previousHeaderStyle,
                  tableStyle: previousTableStyle
                },
                after: {
                  ...action
                }
              }}});
            }
            console.log("Changes in table store :: ", store.get());
          }
        } else if (action.entity === 'topBlock' || action.entity === 'bottomBlock') {
          if (action.op === 'add') {
            let extraChanges = {};
            let extraBlocks = {};
            if (action.entity === 'topBlock') {
              extraChanges = { showtopBlockOnAllPages: true };
            }
            if (blockDetails.style.tableProperties[action.entity] === undefined) {
              let newUUID = uuidv4();
              extraChanges = { ...extraChanges, [action.entity]: newUUID };
              extraBlocks = { [newUUID]: DEFAULT_CREATIVE_BLOCK(newUUID, true, blockUUID) }; // TODO : Include a new creative block
            }
            const newTableProperties = Object.assign({}, blockDetails.style.tableProperties, {
              [action.entity === 'topBlock' ? 'showTopBlock' : 'showBottomBlock']: true,
              ...extraChanges
            });
            const otherChildBlocks = [];
            if (newTableProperties.showTopBlock) {
              otherChildBlocks.push(action.entity === 'topBlock' && Object.keys(extraBlocks).length > 0
                ? extraBlocks[newTableProperties.topBlock]
                : store.get()[newTableProperties.topBlock]);
            }
            if (newTableProperties.showBottomBlock) {
              otherChildBlocks.push(action.entity === 'bottomBlock' && Object.keys(extraBlocks).length > 0
                ? extraBlocks[newTableProperties.bottomBlock]
                : store.get()[newTableProperties.bottomBlock]);
            }
            console.log({ otherChildBlocks, extraBlocks, newTableProperties });
            const { variables } = getCreativeBlockVariableContract(
              blockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              newTableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                style: Object.assign({}, blockDetails.style, {
                  tableProperties: newTableProperties
                }),
                variableContract: variables
              }),
              ...extraBlocks,
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'delete',
                  entity: action.entity
                },
                after: {
                  ...action,
                  entity: action.entity
                }
              }}});
            }
          } else if (action.op === 'delete') {
            let extraChanges = {};
            if (action.entity === 'topBlock') {
              extraChanges = { showtopBlockOnAllPages: false };
            }
            const newTableProperties = Object.assign({}, blockDetails.style.tableProperties, {
              [action.entity === 'topBlock' ? 'showTopBlock' : 'showBottomBlock']: false,
              ...extraChanges
            });
            const otherChildBlocks = [];
            if (newTableProperties.showTopBlock) {
              otherChildBlocks.push(store.get()[newTableProperties.topBlock]);
            }
            if (newTableProperties.showBottomBlock) {
              otherChildBlocks.push(store.get()[newTableProperties.bottomBlock]);
            }
            const { variables } = getCreativeBlockVariableContract(
              blockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              newTableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                style: Object.assign({}, blockDetails.style, {
                  tableProperties: newTableProperties
                }),
                variableContract: variables
              }),
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'add',
                  entity: action.entity
                },
                after: {
                  ...action,
                  entity: action.entity
                }
              }}});
            }
          }
          console.log("Changes in table store :: ", store.get());
        } else if (action.entity === 'groupHeader') {
          const otherChildBlocks = [];
          if (blockDetails.style.tableProperties.showTopBlock) {
            otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.topBlock]);
          }
          if (blockDetails.style.tableProperties.showBottomBlock) {
            otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.bottomBlock]);
          }
          if (action.op === 'add') {
            let extraChanges = {};
            if (!blockDetails.contents[0]?.groupHeaderContent) {
              extraChanges = {
                contents: blockDetails.contents.map(tableColumn => Object.assign({}, tableColumn, {
                  groupHeaderContent: Object.assign({}, tableColumn.headerContent, {
                    columnSpan: 1
                  })
                }))
              };
            }
            const newTableProperties = Object.assign({}, blockDetails.style.tableProperties, {
              showGroupHeaderContent: true
            });
            const newTableContents = extraChanges?.contents ? extraChanges.contents : blockDetails.contents;
            const { variables } = getCreativeBlockVariableContract(
              newTableContents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              newTableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                style: Object.assign({}, blockDetails.style, {
                  tableProperties: newTableProperties
                }),
                ...extraChanges,
                variableContract: variables
              }),
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'delete',
                  entity: 'groupHeader'
                },
                after: {
                  ...action,
                }
              }}});
            }
          } else if (action.op === 'delete') {
            const newTableProperties = Object.assign({}, blockDetails.style.tableProperties, {
              showGroupHeaderContent: false
            });
            if (!newTableProperties.showHeader && !newTableProperties.showGroupHeaderContent) {
              newTableProperties.showHeaderOnAllPages = false;
            }
            const { variables } = getCreativeBlockVariableContract(
              blockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              newTableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                style: Object.assign({}, blockDetails.style, {
                  tableProperties: newTableProperties
                }),
                variableContract: variables
              }),
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'add',
                  entity: 'groupHeader'
                },
                after: {
                  ...action,
                }
              }}});
            }
          } else if (action.op === 'merge') {
            const previousGroupHeader = blockDetails.contents.map(column => column.groupHeaderContent ? Object.assign({}, column.groupHeaderContent) : null);
            let groupHeaderIndex = -1;
            const selectedGroupHeaders = blockDetails.contents.filter(column => column.groupHeaderContent).slice(action.startIndex, action.startIndex + action.mergeLength);
            console.log({ selectedGroupHeaders });
            const mergeLength = selectedGroupHeaders.reduce((acc, col) => acc + (col.groupHeaderContent.columnSpan || 1), 0);
            console.log({ mergeLength });
            const newTableContents = blockDetails.contents.map((column, columnIndex) => {
              if (column.groupHeaderContent) {
                groupHeaderIndex += 1;
                if (groupHeaderIndex === action.startIndex) {
                  return Object.assign({}, column, {
                    groupHeaderContent: Object.assign({}, column.groupHeaderContent, {
                      columnSpan: mergeLength
                    })
                  });
                } else if (groupHeaderIndex > action.startIndex && groupHeaderIndex < action.startIndex + action.mergeLength) {
                  return Object.assign({}, column, {
                    groupHeaderContent: null
                  });
                }
              }
              return column;
            });
            const { variables } = getCreativeBlockVariableContract(
              newTableContents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newTableContents,
                variableContract: variables
              })
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: 'groupHeader',
                  contents: previousGroupHeader
                },
                after: {
                  op: 'replace',
                  entity: 'groupHeader',
                  contents: newTableContents.map(column => column.groupHeaderContent ? Object.assign({}, column.groupHeaderContent) : null)
                }
              }}});
            }
            console.log("Merge changes :: ", newTableContents, store.get()[blockUUID]);
          } else if (action.op === 'split') {
            const previousGroupHeader = blockDetails.contents.map(column => column.groupHeaderContent ? Object.assign({}, column.groupHeaderContent) : null);
            let groupHeaderIndex = -1;
            let groupHeader;
            let startElementFound = false;
            const newTableContents = blockDetails.contents.map((column, columnIndex) => {
              if (column.groupHeaderContent) {
                groupHeaderIndex += 1;
                if (groupHeaderIndex === action.startIndex) {
                  startElementFound = true;
                  groupHeader = JSON.parse(JSON.stringify(column.groupHeaderContent));
                  return Object.assign({}, column, {
                    groupHeaderContent: Object.assign({}, column.groupHeaderContent, {
                      columnSpan: 1
                    })
                  });
                }
              }
              console.log("newContent :: ", startElementFound, groupHeaderIndex, columnIndex);
              if (startElementFound && groupHeaderIndex < action.startIndex + action.mergeLength) {
                console.log({ groupHeader });
                return Object.assign({}, column, {
                  groupHeaderContent: Object.assign({}, groupHeader, {
                    columnSpan: 1
                  })
                });
              }
              return column;
            });
            const { variables } = getCreativeBlockVariableContract(
              newTableContents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newTableContents,
                variableContract: variables
              })
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: 'groupHeader',
                  contents: previousGroupHeader
                },
                after: {
                  op: 'replace',
                  entity: 'groupHeader',
                  contents: newTableContents.map(column => Object.assign({}, column.groupHeaderContent ? Object.assign({}, column.groupHeaderContent) : null))
                }
              }}});
            }
            console.log("Split changes :: ", newTableContents, store.get()[blockUUID]);
          } else if (action.op === 'replace') {
            const previousGroupHeader = blockDetails.contents.map(column => column.groupHeaderContent ? Object.assign({}, column.groupHeaderContent) : null);
            const newTableContents = blockDetails.contents.map((column, columnIndex) => {
              return Object.assign({}, column, {
                groupHeaderContent: action.contents[columnIndex]
              });
            });
            const { variables } = getCreativeBlockVariableContract(
              newTableContents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
              allVariables,
              blockDetails?.style?.summary,
              false,
              blockDetails.variableContract[0].name,
              otherChildBlocks,
              blockDetails.style.tableProperties,
              blockDetails
            );
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                contents: newTableContents,
                variableContract: variables
              })
            });
            if (isUserAction) {
              historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  op: 'replace',
                  entity: 'groupHeader',
                  contents: previousGroupHeader
                },
                after: {
                  ...action,
                }
              }}});
            }
          }
        }
        return;
      case 'height':
        // TODO : check if other elements are affected and return if change is not possible
        const headerHeight = blockDetails.type === HEADER && flow[HEADER] ? details.height : store.get()[flow[HEADER]]?.style?.height;
        const footerHeight = blockDetails.type === FOOTER && flow[FOOTER] ? details.height : store.get()[flow[FOOTER]]?.style?.height;
        const newContentAreaHeight = flow.height - (flow.margin[0] + flow.margin[2] + ((headerHeight || 0) + (footerHeight || 0)));
        let newStore = undefined; 
        const reusableBlockType= store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE];
        const flowElement = document.getElementById(details.flowUUID);
        if(details.reusableSection && (reusableBlockType === HEADER  || reusableBlockType === FOOTER)){
          const maxHeight = flow.height < details.height;
          if(maxHeight){
            message.error(t(COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEADER_FOOTER_MAX_HEIGHT_ERROR.key, COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEADER_FOOTER_MAX_HEIGHT_ERROR.value, {value: reusableBlockType === HEADER ? 'Header' : 'Footer' }),6);  
            return false;
          }
        }
        
        if(details.type === HEADER || details.type === FOOTER){
          const maxHeaderFooterHeightReached = flow.height < ((headerHeight || 0) + (footerHeight || 0));
          if(maxHeaderFooterHeightReached){
            message.error(t(COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEADER_FOOTER_MAX_HEIGHT_ERROR.key, COMPLEX_PAGE_OPTIONS.CREATIVE_BLOCK_HEADER_FOOTER_MAX_HEIGHT_ERROR.value, {value: details.type === HEADER ? 'Header' : 'Footer' }),6);  
            return false;
          }
        }
        
        const previousValue = blockDetails.style.height;
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            style: Object.assign({}, blockDetails.style, {
              height: details.height
            })
          })
        });
        const eventDetails ={
          newContentAreaHeight,
          updatedbBlockheight:details.height,
          blockId: blockUUID,
        }
        flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight', { detail: eventDetails }));
        console.log("Change in store :: ", store.get());
        const entity = details.type === CREATIVE_BLOCK ? 'block' : details.type;
        if (isUserAction) {
          historyStore.set({ changes: { entity, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [entity]: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: previousValue,
            after: details.height
          }}});
        }
        return true;
        case 'backgroundColor':
          const previousColor = blockDetails.style.backgroundColor;
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              style: Object.assign({}, blockDetails.style, {
                backgroundColor: details.backgroundColor
              })
            })
          });
          console.log("Change in store :: ", store.get());
          if (isUserAction) {
            const entity = (details.type === HEADER || details.type === FOOTER) ? details.type : 'block';
            historyStore.set({ changes: { entity, operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: details.flowUUID,
              [entity]: {
                style: Object.assign({}, blockDetails.style, {
                  backgroundColor: details.backgroundColor
                }),
                uuid: blockUUID,
                key: blockOperation,
              },
              before: previousColor,
              after: details.backgroundColor
            }}});
          }
        return true;
        case 'alignment':
          const previousAlignment = blockDetails.style.alignment;
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              style: Object.assign({}, blockDetails.style, {
                alignment: details.alignment
              })
            })
          });
          console.log("Change in store :: ", store.get());
          if (isUserAction) {
            const entity = (details.type === HEADER || details.type === FOOTER) ? details.type : 'block';
            historyStore.set({ changes: { entity, operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: details.flowUUID,
              [entity]: {
                style: Object.assign({}, blockDetails.style, {
                  alignment: details.alignment
                }),
                uuid: blockUUID,
                key: blockOperation,
              },
              before: previousAlignment,
              after: details.alignment
            }}});
          }
        return true;
        case 'padding':
          if (details.type === CREATIVE_BLOCK) {
            newStore = JSON.parse(JSON.stringify(store.get()));
            newStore[blockUUID].style.padding = details.padding;
          }
          const previousPadding = Object.assign({}, blockDetails.style.padding);
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              style: Object.assign({}, blockDetails.style, {
                padding: details.padding
              })
            })
          });
          if (isUserAction) {
            const entity = 'block';
            historyStore.set({ changes: { entity, operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: details.flowUUID,
              [entity]: {
                style: Object.assign({}, blockDetails.style, {
                  padding: details.padding
                }),
                uuid: blockUUID,
                key: blockOperation,
              },
              before: previousPadding,
              after: details.padding
            }}});
          }
        return true;
      case 'element':
        const { uuid, ...updatedDetails } = details.element;
        let previousEl;
        // Logic for storing changes within elements in complex creative blocks
        let elementId = -1;
        if (Array.isArray(blockDetails.contents)) {
          elementId = blockDetails.contents.findIndex(el => el.uuid === uuid);
        }
        if (elementId !== -1) {
          const newContents = blockDetails.contents.map((el, index) => {
            if (index === elementId) {
              previousEl = JSON.parse(JSON.stringify(el));
              return Object.assign({}, el, updatedDetails);
            } else {
              return el;
            }
          });
          const isRepetitive = blockDetails.variableContract?.length > 0 ? blockDetails.variableContract[0].type === 'object' : false;
          const { variables } = getCreativeBlockVariableContract(newContents, allVariables, blockDetails?.style?.summary, isRepetitive, isRepetitive ? blockDetails.variableContract[0].name : '' );
          const variableContract = variables;

          let changes = details;
          let extraChanges = {
            before: changes.before,
            after: changes.after
          };
          console.log({ extraChanges: JSON.parse(JSON.stringify(extraChanges)) });
          if (changes.textChanges) {
            extraChanges = {
              undoLength: changes.undoLength,
              redoLength: changes.redoLength,
              textOperation: changes.textOperation,
              before: { uuid: changes.elementUUID, parentUUID: uuid, style: changes.before.style, isGroupHeader: changes.isGroupHeader, isRow: changes.isRow },
              after: { uuid: changes.elementUUID, parentUUID: uuid, style: changes.after.style, isGroupHeader: changes.isGroupHeader, isRow: changes.isRow },
            }
          }

          changes = {
            element: {
              uuid: changes.elementUUID,
            },
            textChanges: changes.textChanges,
            callback: changes.callback,
            ...extraChanges
          }

          let parentBlockChanges = {};
          if (blockDetails.isChild && blockDetails.parentBlockUUID) {
            const parentBlockDetails = store.get()[blockDetails.parentBlockUUID];
            const otherChildBlocks = [];
            if (parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
              let otherBlock = 'topBlock';
              if (blockDetails.uuid === parentBlockDetails.style.tableProperties.topBlock) {
                otherBlock = 'bottomBlock';
              }
              if (otherBlock === 'topBlock' && parentBlockDetails.style.tableProperties.showTopBlock) {
                otherChildBlocks.push(store.get()[parentBlockDetails.style.tableProperties.topBlock]);
              } else if (otherBlock === 'bottomBlock' && parentBlockDetails.style.tableProperties.showBottomBlock) {
                otherChildBlocks.push(store.get()[parentBlockDetails.style.tableProperties.bottomBlock]);
              }
            }
            console.log({ otherChildBlocks });
            const isParentRepetitive = parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK ? false : parentBlockDetails.variableContract[0].type === 'object';
            const { variables: parentVariableContract } = getCreativeBlockVariableContract(
              parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK
                ? parentBlockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' }))
                : parentBlockDetails.contents,
              allVariables, parentBlockDetails?.style?.summary, isParentRepetitive, parentBlockDetails.variableContract[0].name || '',
              [
               Object.assign({}, blockDetails, {
                 contents: newContents,
                 variableContract
               }),
               ...otherChildBlocks,
              ], parentBlockDetails.style.tableProperties, parentBlockDetails);
            parentBlockChanges[blockDetails.parentBlockUUID] = Object.assign({}, parentBlockDetails, {
              variableContract: parentVariableContract
            })
          }
          console.log({ parentBlockChangesInElement: parentBlockChanges });
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              contents: newContents,
              variableContract
            }),
            ...parentBlockChanges
          });
          if (isUserAction) { // TODO : Text operations for undo-redo has to be done similar to contents
          const entity = details.type === CREATIVE_BLOCK || details.type === DYNAMIC_TABLE_BLOCK ? 'block' : details.type;
          historyStore.set({ changes: { entity, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [entity]: {
              uuid: blockUUID,
              key: changes !== undefined ? changes.textChanges ? 'text-operations' : 'element' : blockOperation,
            },
            before: previousEl,
            after: details.element,
            ...(changes || {})
            },
            undoLength: changes?.undoLength,
            redoLength: changes?.redoLength,
            textOperation: changes?.textOperation
          }});
          } else {

            if (document.getElementById(`img-${uuid}`)) {
              document.getElementById(`img-${uuid}`).dispatchEvent(new CustomEvent('handleEditorUndoRedo', { detail: { element: details.element } }));
            }
            // TODO : Dispatch event only on actual resizing related undo redo, otherwise unnecessary undo / redo call made to text cells
            if (details.tableChanges && details.element.contentType === STATIC_TABLE_ELEMENT) {
              document.getElementById(uuid).dispatchEvent(
                new CustomEvent('tableScalingEnd', { detail: { newContents: details.element.contents, action: details.op } })
              )
            }
            if (details.callback) {
              details.callback({ uuid: uuid, ...updatedDetails.style });
            }
          }
          return true;
        } else if (blockDetails.uuid === uuid && blockDetails.isChild) {
          console.log("Block Details uuid is the same as element!! ", blockDetails, details);
          let parentBlockDetails = store.get()[blockDetails.parentBlockUUID];
          if (parentBlockDetails.type === STATIC_TABLE_BLOCK) {
            store.set({
              [blockDetails.uuid]: Object.assign({}, blockDetails, updatedDetails)
            });

            parentBlockDetails = store.get()[blockDetails.parentBlockUUID];
            const actualContents = getStoreStaticTableContents(parentBlockDetails?.contents, store.get());
            const { variables } = getCreativeBlockVariableContract(actualContents || [], allVariables, parentBlockDetails?.style?.summary);
            store.set({
              [parentBlockDetails.uuid]: Object.assign({}, parentBlockDetails, {
                variableContract: variables,
              }),
            });
            if (document.getElementById(`img-${uuid}`)) {
              document.getElementById(`img-${uuid}`).dispatchEvent(new CustomEvent('handleEditorUndoRedo', { detail: { element: details.element } }));
            }
            return true;
          }
        }
        return false;
      case 'text-operations': {
        const { op } = details;
        let editorId = blockUUID;
        let parentElementUUID;
        let extraDetails = {};
        let contentKey = undefined;
        if (details['text-operations']) {
          editorId = details['text-operations']['uuid'];
          parentElementUUID = details['text-operations']['parentUUID'];
          extraDetails.style = details['text-operations'].style;
          if (blockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
            contentKey = 'headerContent';
            if (details['text-operations']['isGroupHeader']) {
              contentKey = 'groupHeaderContent';
            } else if (details['text-operations']['isRow']) {
              contentKey = 'content';
            }
          }
          // Trying if resize handlers can be re-rendered same time or before font change
          store.set({
            [blockUUID]: Object.assign({}, blockDetails, {
              contents: blockDetails.contents.map((el, index) => {
                if (el.uuid === editorId) {
                  previousEl = JSON.parse(JSON.stringify(el));
                  if (contentKey) {
                    return Object.assign({}, el, {
                      [contentKey]: Object.assign({}, el[contentKey], {
                        style: details['text-operations'].style
                      })
                    });
                  }
                  return Object.assign({}, el, { style: details['text-operations'].style });
                } else {
                  return el;
                }
              })
            })
          });
          if(details.callback)
          details.callback({ uuid: editorId, ...extraDetails.style });
        }
        if (contentKey === 'groupHeaderContent') {
          editorId = `gh-text-${editorId}`;
        } else if (contentKey === 'content') {
          editorId = `${editorId}-row`;
        }
        console.log("text-operations :: ", op, editorId, details, contentKey);
        if (document.getElementById(`toolbar-${editorId}`)) {
          document.getElementById(`toolbar-${editorId}`).dispatchEvent(new CustomEvent('handleEditorUndoRedo', { detail: { action: op, ...extraDetails  } }));
        }
        console.log("Text operations context change for undo redo within text blocks!! ", store.get()[blockUUID]);
        return;
      }
      case 'table-layout-changes':
        const { uuid: gridBlockUUID, contents: gridBlockContents, style: gridStyle, variableContract: gridVariableContract } = details['table-layout-changes'];
        store.set({
          [gridBlockUUID]: Object.assign({}, blockDetails, {
            contents: gridBlockContents,
            style: gridStyle,
            variableContract: gridVariableContract
          })
        });
        return;
      case 'text-contents':
        const { contents, historyChange, isInteractiveDesign = false } = details;
        let varList = [];
        if (details.type === 'block') {
          if (isInteractiveDesign) {
            varList = blockDetails.variableContract;
          } else {
            const isRepetitive = blockDetails.variableContract?.length > 0 ? blockDetails.variableContract[0].type === 'object' : false;
            const { variables } = getCreativeBlockVariableContract(contents, allVariables, blockDetails?.style?.summary, isRepetitive, isRepetitive ? blockDetails.variableContract[0].name : '' );
            varList = variables;
          }
        }
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            contents,
            variableContract: varList
          })
        });
        const textEntity = 'block'; // TODO : Handle for text elements
        historyStore.set({ changes: {
          entity: textEntity,
          operation: 'update',
          data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [textEntity]: {
              uuid: blockUUID,
              key: 'text-operations',
            }
          },
          undoLength: historyChange.undo,
          redoLength: historyChange.redo,
          textOperation: historyChange.op
        }});
        return;
      case 'contents':
        const previousList = blockDetails.contents;
        let newContents = details.contents;
        let changes;
        let parentBlockDetails;
        let blockChanges = {};
        let parentBlockChanges = {};
        let response = details.contents;
        if (blockDetails.isChild && blockDetails.parentBlockUUID) {
          parentBlockDetails = store.get()[blockDetails.parentBlockUUID];
        }
        if (typeof details.contents === 'function') {
          response = details.contents(blockDetails.type === STATIC_TABLE_BLOCK || parentBlockDetails?.type === STATIC_TABLE_BLOCK ? [blockDetails] : deepCopy(blockDetails.contents));
        }
          if (response.elements) { // Return from updateComplexElementDetails
            if (!response.hasChanged) {
              return;
            }
            changes = response;
            newContents = changes.elements;
            let extraChanges = {
              before: changes.before,
              after: changes.after
            };
            console.log({ extraChanges: JSON.parse(JSON.stringify(extraChanges)) });
            if (changes.textChanges) {
              extraChanges = {
                undoLength: changes.undoLength,
                redoLength: changes.redoLength,
                textOperation: changes.textOperation,
                before: { uuid: changes.elementUUID, style: changes.before.style, isGroupHeader: changes.isGroupHeader, isRow: changes.isRow },
                after: { uuid: changes.elementUUID, style: changes.after.style, isGroupHeader: changes.isGroupHeader, isRow: changes.isRow },
              }
            }
            changes = {
              element: {
                uuid: changes.elementUUID,
              },
              textChanges: changes.textChanges,
              tableChanges: changes.tableChanges,
              callback: changes.callback,
              ...extraChanges
            }
          } else {
            newContents = response;
          }
        let variablesList = [];
        console.log("Update block contents :: ", details, changes, newContents);
        let groupsArray;
        if (blockDetails.type === CREATIVE_BLOCK || blockDetails.type === HEADER || blockDetails.type === FOOTER) {
          const isRepetitive = blockDetails.variableContract?.length > 0 ? blockDetails.variableContract[0].type === 'object' : false;
          const { variables } = getCreativeBlockVariableContract(newContents, allVariables, blockDetails?.style?.summary, isRepetitive, isRepetitive ? blockDetails.variableContract[0].name : '' );
          variablesList = variables;
          groupsArray = getGroupsFromMap(newContents, element_groups);
          if (response?.extraFormElementsUuids) {
            changes.before = Object.assign(
              {},
              { elements: blockDetails.contents, hasChanged: true },
            );
            changes.after = Object.assign({}, { ...response });
          }
        } else if (blockDetails.type === DYNAMIC_TABLE_BLOCK) {
          console.log("coming here !!! :: ", blockDetails.variableContract[0].name);
          // TODO : Can dynamic tables be repetitive?
          const otherChildBlocks = [];
          if (blockDetails.style.tableProperties.showTopBlock) {
            otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.topBlock]);
          }
          if (blockDetails.style.tableProperties.showBottomBlock) {
            otherChildBlocks.push(store.get()[blockDetails.style.tableProperties.bottomBlock]);
          }
          const { variables } = getCreativeBlockVariableContract(
            newContents.map(column => Object.assign({}, column, { contentType: 'tableHeader' })),
            allVariables,
            blockDetails?.style?.summary,
            false,
            blockDetails.variableContract[0].name,
            otherChildBlocks,
            blockDetails.style.tableProperties,
            blockDetails
          );
          console.log({ variablesList });
          variablesList = variables;
        } else if (blockDetails.type === STATIC_TABLE_BLOCK) {
          blockChanges = {
            style: newContents[0].style
          };
          const before = JSON.parse(JSON.stringify(newContents));
          const cellContents = [...before[0].contents];
          console.log("Coming here for static table block :: ", before);
          newContents = before[0].contents.map(cell => {
            return Object.assign({}, cell, { contents: [] });
          });
          const actualContents = cellContents.map(cell => {
             if (typeof cell.contents === 'string') {
               const existingCellContents = store.get()[cell.contents];
               // console.log("existing cell contents :: ", existingCellContents)
               if (existingCellContents) {
                 parentBlockChanges[cell.uuid] = Object.assign({}, existingCellContents, { uuid: cell.uuid });
               }
               return parentBlockChanges[cell.uuid];
             } else if (cell.contents?.length > 0) { // Elements directly dropped and added to table
              // Adding to context
              parentBlockChanges[cell.uuid] = cell.contents[0];
              return cell.contents[0];
            }
            return store.get()[cell.uuid];
          }).filter(cell => cell !== undefined);
          console.log("Coming here for static table block :: ", cellContents, actualContents);
          const { variables } = getCreativeBlockVariableContract(
            actualContents,
            allVariables,
            blockDetails?.style?.summary
          );
          console.log({ variablesList });
          variablesList = variables;
          changes.before = Object.assign({}, blockDetails);
          changes.after = Object.assign({}, blockDetails, {
            contents: newContents,
            variableContract: variablesList,
            ...blockChanges
          });
        } else if (parentBlockDetails.type === STATIC_TABLE_BLOCK) {
          const { variables } = getCreativeBlockVariableContract(
            newContents,
            allVariables,
            blockDetails?.style?.summary
          );
          variablesList = variables;
          console.log({ newContents, variablesList });
          blockChanges = Object.assign({}, newContents[0], {
            variableContract: variablesList
          });
        }
          if (blockDetails.isChild && blockDetails.parentBlockUUID) {
            parentBlockDetails = store.get()[blockDetails.parentBlockUUID];
            let otherChildBlocks = [];
            if (parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
              let otherBlock = 'topBlock';
              if (blockDetails.uuid === parentBlockDetails.style.tableProperties.topBlock) {
                otherBlock = 'bottomBlock';
              }
              if (otherBlock === 'topBlock' && parentBlockDetails.style.tableProperties.showTopBlock) {
                otherChildBlocks.push(store.get()[parentBlockDetails.style.tableProperties.topBlock]);
              } else if (otherBlock === 'bottomBlock' && parentBlockDetails.style.tableProperties.showBottomBlock) {
                otherChildBlocks.push(store.get()[parentBlockDetails.style.tableProperties.bottomBlock]);
              }
            } else if (parentBlockDetails.blockType === STATIC_TABLE_BLOCK) {
              otherChildBlocks = parentBlockDetails.contents.filter(cell => cell.uuid !== blockDetails.uuid).map(cell => store.get()[cell.uuid]).filter(e => e !== undefined);
            }
            console.log({ otherChildBlocks });
            const isParentRepetitive = parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK ? false : parentBlockDetails.variableContract[0]?.type === 'object';
            const { variables: parentVariableContract } = getCreativeBlockVariableContract(
              parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK
                ? parentBlockDetails.contents.map(column => Object.assign({}, column, { contentType: 'tableHeader' }))
                : parentBlockDetails.contents,
              allVariables, parentBlockDetails?.style?.summary, isParentRepetitive, parentBlockDetails.variableContract[0]?.name || '',
              [
               Object.assign({}, blockDetails, {
                 contents: newContents,
                 variableContract: variablesList
               }),
               ...otherChildBlocks
              ], parentBlockDetails.blockType === DYNAMIC_TABLE_BLOCK ? parentBlockDetails.style.tableProperties : undefined, parentBlockDetails);
            parentBlockChanges[blockDetails.parentBlockUUID] = Object.assign({}, parentBlockDetails, {
              variableContract: parentVariableContract
            })
          }
        // console.log("Context elements changes :: ", details, variablesList);
        console.log({ parentBlockChangesInContents: parentBlockChanges });
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            contents: newContents,
            variableContract: variablesList,
            groups: groupsArray,
            ...blockChanges
          }),
          ...parentBlockChanges
        });
        console.log("Changes in store after contents :: ", JSON.parse(JSON.stringify(store.get())))
        if (isUserAction) {
          const entity = blockDetails.type === HEADER || blockDetails.type === FOOTER ? blockDetails.type : 'block';
          // const entity = 'block';
          historyStore.set({ changes: { entity, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [entity]: {
              uuid: blockUUID,
              key: changes !== undefined
                ? changes.textChanges
                  ? 'text-operations'
                  : blockDetails.type === STATIC_TABLE_BLOCK
                     ? 'table-layout-changes'
                     : !response?.extraFormElementsUuids
                       ? 'element'
                       : blockOperation
                : blockOperation,
            },
            before: previousList,
            after: newContents,
            ...(changes || {})
            },
            undoLength: changes?.undoLength,
            redoLength: changes?.redoLength,
            textOperation: changes?.textOperation
          }});
        }
        return;
      case 'static-table-cells':
        console.log("static-table-quick-operations ::: ", details);
        const cellDetails = details['static-table-cells'] || details;
        const updateVariableContractofStaticTableBlock = () => {
          const actualContents = getStoreStaticTableContents(blockDetails?.contents, store.get());
          const { variables } = getCreativeBlockVariableContract(
            actualContents || [],
            allVariables,
            blockDetails?.style?.summary
          );
          store.set({ [blockUUID]: { ...blockDetails, variableContract: variables } });
        };
        if (cellDetails.type === 'drop') {
          store.set({
            [cellDetails.elementUUID]: cellDetails.droppedElement
          });
          updateVariableContractofStaticTableBlock();
          if (isUserAction) {
            historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: cellDetails.flowUUID,
              block: {
                uuid: blockUUID,
                key: blockOperation
              },
              before: {
                ...cellDetails,
                type: 'delete'
              },
              after: {
                ...cellDetails,
                type: 'drop',
              }
            }}})
          }
        } else if (cellDetails.type === 'delete') {
          store.set({
            [cellDetails.elementUUID]: undefined
          });
          updateVariableContractofStaticTableBlock();
          if (isUserAction) {
            historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: cellDetails.flowUUID,
              block: {
                uuid: blockUUID,
                key: blockOperation
              },
              before: {
                ...cellDetails,
                type: 'drop'
              },
              after: {
                ...cellDetails,
                type: 'delete',
              }
            }}})
          }
        } else if (cellDetails.type === 'update') {
          const oldCellData = store.get()[cellDetails.elementUUID];
          store.set({
            [cellDetails.elementUUID]: cellDetails.droppedElement,
          });
          if (isUserAction) {
            historyStore.set({
              changes: {
                entity: 'block',
                operation: 'update',
                data: {
                  uuid: blockUUID,
                  flowUUID: cellDetails.flowUUID,
                  block: {
                    uuid: blockUUID,
                    key: blockOperation,
                  },
                  before: {
                    ...cellDetails,
                    ...(oldCellData && { droppedElement: { ...oldCellData } }),
                    type: oldCellData ? 'update' : 'delete',
                  },
                  after: {
                    ...cellDetails,
                    type: 'update',
                  },
                },
              },
            });
          }
          if (details.isTextElement && details.alreadyHasData) {
            document.dispatchEvent(
              new CustomEvent('gridCellUpdate', {
                detail: { pasteCellUUID: details.elementUUID, action: details.op },
              }),
            );
          }
        } 
        return;
      case 'static-table-cells-form-options': {
        if (details[blockOperation]) {
          details = details[blockOperation];
        }
        const beforeObject = {obj: {}, variableContract: []};
        if (details?.beforeObject?.obj) {
          const storeObj = { ...deepCopy(details.beforeObject.obj) };
          if (details.beforeObject.variableContract) {
            storeObj[blockUUID] = {
              ...blockDetails,
              variableContract: details.beforeObject.variableContract,
            };
          }
          store.set(storeObj);
        } else {
          const storeObj = {};
          const currentStore = store.get();
          let currentElement = deepCopy(currentStore[details.uuid]);
          beforeObject.obj[details.uuid] = deepCopy(currentElement);
          beforeObject.variableContract = deepCopy(blockDetails.variableContract);
          if (details.style) {
            if (details.style.width && typeof details.style.width === 'string') {
              details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
            }
            if (details.style.height && typeof details.style.height === 'string') {
              details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
            }
            currentElement.style = {
              ...currentElement.style,
              ...details.style,
            };
          }
          currentElement = {
            ...currentElement,
            formOptions: { ...currentElement.formOptions, ...details.options },
          };
          const { variables: variableList } = getCreativeBlockVariableContract(
            [currentElement],
            allVariables,
            blockDetails?.style?.summary
          );
          currentElement.variableContract = variableList;
          storeObj[details.uuid] = currentElement;
          if (details?.extraFormElementsUuids?.length) {
            details.extraFormElementsUuids.forEach(elUuid => {
              const extraElement = deepCopy(currentStore[elUuid]);
              beforeObject.obj[elUuid] = deepCopy(extraElement);
              if (extraElement) {
                if (details?.style) {
                  extraElement.style = {
                    ...extraElement.style,
                    ...details.style,
                  };
                }
                if (
                  details.options &&
                  (CHECK_TYPE_KEY in details.options || READ_ONLY_KEY in details.options)
                ) {
                  extraElement.formOptions = {
                    ...extraElement.formOptions,
                    ...details.options,
                  };
                }
                if (details.negativeOptions && IS_CHECKED_KEY in details.negativeOptions) {
                  extraElement.formOptions = {
                    ...extraElement.formOptions,
                    ...details.negativeOptions,
                  };
                }
              }
              const { variables } = getCreativeBlockVariableContract([extraElement], allVariables, blockDetails?.style?.summary);
              extraElement.variableContract = variables;
              storeObj[elUuid] = extraElement;
            });
          }
          store.set(storeObj);
          const actualContents = getStoreStaticTableContents(blockDetails?.contents, store.get());
          const { variables } = getCreativeBlockVariableContract(
            actualContents || [],
            allVariables,
            blockDetails?.style?.summary
          );
          store.set({ [blockUUID]: { ...blockDetails, variableContract: variables } });
        }
        if(isUserAction) {
          const entity = 'block';
          historyStore.set({ changes: { entity, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [entity]: {
              uuid: blockUUID,
              key: blockOperation,
            },
            before: {beforeObject},
            after: details,
            },
          }});
        }
        return;
      }
      case 'spacing':
        const previousSpacing = blockDetails.style.spacing;
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            style: Object.assign({}, blockDetails.style, {
              spacing: details.spacing
            })
          })
        });
        console.log("Change in store :: ", store.get());
        if (isUserAction) {
          const entity = details.type === LINK_BLOCK ? 'block' : details.type;
          historyStore.set({ changes: { entity, operation: 'update', data: {
            uuid: blockUUID,
            flowUUID: details.flowUUID,
            [entity]: {
              style: Object.assign({}, blockDetails.style, {
                spacing: details.spacing
              }),
              uuid: blockUUID,
              key: blockOperation,
            },
            before: previousSpacing,
            after: details.spacing
          }}});
        }
        return true;
      
      case 'static-table-text-alignment':
        const storeChanges = {};
        const prevCellContent = [];
        const updatedCellContent = [];
        const dispatchTextAlignEvent = (uuid) => {
          const event = new CustomEvent('alignText', { detail: {
            uuid,
            action: details.op,
            ...(details.op === 'update' && { position: details.position === 'left' ? '' : details.position }),         
          } });
          document.dispatchEvent(event);
        }
        if(details.op === 'update') {
          let tableBlock = deepCopy(store.get()[blockUUID]);
          tableBlock.contents = tableBlock.contents.map(element => {
            if(details.elementUUID === element.uuid) {
              return Object.assign({}, element, {
                contents: element.contents.map(cell => {
                  if(details.selectedCellUUIDs.includes(cell.uuid)) {
                    prevCellContent.push(deepCopy(cell));
                    let updatedCell;
                    if(details.axis === 'x') {
                      dispatchTextAlignEvent(cell.uuid);
                      updatedCell = Object.assign({}, cell, {
                        contents: cell.contents.map(content => {
                          if(content.contentType === "LINE_BREAK") {
                            return { ...content, style: { ...content.style, alignment: details.position } };
                          }
                          return content;
                        })
                      });
                      updatedCellContent.push(updatedCell);
                      return updatedCell;
                    } else {
                      updatedCell = { ...cell, style: { ...cell.style, verticalAlignment: details.position }};
                      updatedCellContent.push(updatedCell);
                      return updatedCell;
                    }
                  }
                  return cell;
                })
              });
            }
            return element;
          });
          storeChanges[blockUUID] = tableBlock;
        } else {
          let tableBlock = deepCopy(store.get()[blockUUID]);
          tableBlock.contents = tableBlock.contents.map(element => {
            if(details[blockOperation].elementUUID === element.uuid) {
              return Object.assign({}, element, {
                contents: element.contents.map(item => {
                  if(details[blockOperation].cellContent.some(content => content.uuid === item.uuid)) {
                    if(details[blockOperation].axis === 'x') {
                      dispatchTextAlignEvent(item.uuid);
                    }
                    return details[blockOperation].cellContent.find(content => content.uuid === item.uuid);
                  }
                  return item;
                })
              });
            }
            return element;
          })
          storeChanges[blockUUID] = tableBlock;
        }
        store.set(Object.assign({}, store.get(), storeChanges));
        if(isUserAction) {
          historyStore.set({
            changes: {
              entity: 'block',
              operation: 'update',
              data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                block: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: {
                  cellContent: prevCellContent,
                  elementUUID: details.elementUUID,
                  axis: details.axis,
                },
                after: {
                  cellContent: updatedCellContent,
                  elementUUID: details.elementUUID,
                  axis: details.axis,
                },
              },
            },
          });
        }
        return;
        case 'static-table-content-alignment':
          const { flowUUID, selectedCellUUIDs, axis, position } = details;
          const changesInStore = {};
          const prevBlockContents = [];
          const updatedBlockContents = [];
          const textCellUUIDs = [];
          const contentCellUUIDs = [];
          let tableBlock = {};
          const dispatchAlignTextEvent = (uuid) => {
            const event = new CustomEvent('alignText', { detail: {
              uuid,
              action: details.op,
              ...(details.op === 'update' && { position: details.position === 'left' ? '' : details.position }),         
            } });
            document.dispatchEvent(event);
          }
          if(details.op === 'update') {
            selectedCellUUIDs.forEach(uuid => {
              const textBlock = store.get()[uuid];       
              if(textBlock !== undefined && textBlock.contentType === "text") {
                textCellUUIDs.push(uuid);
              } else {
                contentCellUUIDs.push(uuid);
              }
            });
            if(axis === 'x') {
              textCellUUIDs.forEach(uuid => {
                prevBlockContents.push(deepCopy(store.get()[uuid]));
                let textBlock = deepCopy(store.get()[uuid]);
                dispatchAlignTextEvent(uuid);
                textBlock.contents = textBlock.contents.map(content => {
                  if(content.contentType === "LINE_BREAK") {
                    return { ...content, style: { ...content.style, alignment: position }};
                  }
                  return content;
                });
                updatedBlockContents.push(textBlock);
                changesInStore[uuid] = textBlock;
              });
            }
            if(contentCellUUIDs.length > 0 || axis === 'y') {
              let contentBlock = deepCopy(blockDetails);
              contentBlock.contents = contentBlock.contents.map(content => {
                if(selectedCellUUIDs.includes(content.uuid)) {
                  return Object.assign({}, content, {
                    style: Object.assign({}, content.style, {
                      ...( axis === 'x' && { horizontalAlignment: position }),
                      ...( axis === 'y' && { verticalAlignment: position }),
                    })
                  });
                }
                return content;
               });
               tableBlock = contentBlock;
               changesInStore[blockUUID] = contentBlock;
            }
          } else {
            details[blockOperation].cellContent.forEach(block => {
              dispatchAlignTextEvent(block.uuid);
              changesInStore[block.uuid] = block;
            })
            changesInStore[blockUUID] = details[blockOperation].tableBlock;
          }
          store.set(Object.assign({}, store.get(), changesInStore));
          if(isUserAction) {
            historyStore.set({
              changes: {
                entity: 'block',
                operation: 'update',
                data: {
                  uuid: blockUUID,
                  flowUUID,
                  block: {
                    uuid: blockUUID,
                    key: blockOperation,
                  },
                  before: {
                    tableBlock: blockDetails,
                    cellContent: prevBlockContents,
                  },
                  after: {
                    tableBlock,
                    cellContent: updatedBlockContents,
                  },
                },
              },
            });
          }
          return;
        case 'linkStyles': {
        const previousLinkStyles = { data: deepCopy(blockDetails.style.linkStyles) };
        if (blockDetails.contents) {
          previousLinkStyles.contents = deepCopy(blockDetails.contents);
        }
        const isRepetitive =
          blockDetails.variableContract?.length > 0
            ? blockDetails.variableContract[0].type === 'object'
            : false;
        const contents = [];
        if (details?.linkStyles?.contents || blockDetails?.contents) {
          contents.push({ contentType: 'text', contents: details.linkStyles.contents || blockDetails?.contents });
        }
        if (details?.linkStyles?.data?.contents) {
          contents.push({ contentType: 'text', contents: details.linkStyles.data.contents });
        }
        const { variables: varList1 } = getCreativeBlockVariableContract(
          contents,
          allVariables,
          blockDetails?.style?.summary,
          isRepetitive,
          isRepetitive ? blockDetails.variableContract[0].name : '',
        );
        const updateObj = {
          [blockUUID]: Object.assign({}, blockDetails, {
            style: Object.assign({}, blockDetails.style, {
              linkStyles: details.linkStyles.data,
            }),
            variableContract: varList1,
          }),
        };
        if (details.linkStyles.contents) {
          updateObj[blockUUID] = Object.assign({}, updateObj[blockUUID], {
            contents: details.linkStyles.contents,
          });
        }
        store.set(updateObj);
        console.log('Change in store :: ', store.get());
        if (isUserAction) {
          const historyObj = {
            style: Object.assign({}, blockDetails.style, {
              linkStyles: details.linkStyles.data,
            }),
            uuid: blockUUID,
            key: blockOperation,
          };
          if (details.linkStyles.contents) {
            historyObj.contents = details.linkStyles.contents;
          }
          const entity = details.type === BUTTON_BLOCK ? 'block' : details.type;
          historyStore.set({
            changes: {
              entity,
              operation: 'update',
              data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                [entity]: historyObj,
                before: previousLinkStyles,
                after: details.linkStyles,
              },
            },
          });
        }
        const buttonblockEl = document.getElementById(`button-block-${blockUUID}`);
        if (buttonblockEl) {
          buttonblockEl.dispatchEvent(
            new CustomEvent('updateLinkStyles')
          )
        }
        return true;
        }
        case 'summary': {
          const { summary, flowUUID } = details;
          const currentStore = store.get();
          const tableProperties =
            blockDetails.type === DYNAMIC_TABLE_BLOCK
              ? blockDetails.style.tableProperties
              : undefined;
          const isRepetitive =
            blockDetails.variableContract?.length > 0
              ? blockDetails.variableContract[0].type === 'object'
              : false;
          const variableName = isRepetitive ? blockDetails.variableContract[0].name : '';
          let contents = blockDetails.contents;
          if (blockDetails.type === STATIC_TABLE_BLOCK) {
            contents = getStoreStaticTableContents(blockDetails.contents, currentStore);
          } else if (blockDetails.type === DYNAMIC_TABLE_BLOCK) {
            contents = blockDetails.contents.map(column =>
              Object.assign({}, column, { contentType: 'tableHeader' }),
            );
          }
          const otherChildBlocks = [];
          if (tableProperties) {
            if (tableProperties.showTopBlock) {
              otherChildBlocks.push(currentStore[tableProperties.topBlock]);
            }
            if (tableProperties.showBottomBlock) {
              otherChildBlocks.push(currentStore[tableProperties.bottomBlock]);
            }
          }
          const { variables } = summary
            ? getCreativeBlockVariableContract(
                contents,
                allVariables,
                summary,
                isRepetitive,
                variableName,
                otherChildBlocks,
                tableProperties,
                blockDetails
              )
            : { variables: [] };
          store.set({
            [blockUUID]: {
              ...blockDetails,
              style: {
                ...blockDetails.style,
                summary: summary,
              },
              variableContract: variables,
            },
          });
          if (isUserAction) {
            const entity = (details.type === HEADER || details.type === FOOTER) ? details.type : 'block';
            historyStore.set({
              changes: {
                entity: entity,
                operation: 'update',
                data: {
                  uuid: blockUUID,
                  flowUUID: flowUUID,
                  [entity]: {
                    uuid: blockUUID,
                    key: blockOperation,
                  },
                  before: blockDetails.style.summary,
                  after: summary,
                },
              },
            });
          }
          break;
        }
        case 'style': {
          const { style, flowUUID } = details;
          store.set({
            [blockUUID]: {
              ...blockDetails,
              style: {
                ...blockDetails.style,
                ...style
              },
            },
          });
          if (isUserAction) {
            const entity = (details.type === HEADER || details.type === FOOTER) ? details.type : 'block';
            historyStore.set({
              changes: {
                entity: entity,
                operation: 'update',
                data: {
                  uuid: blockUUID,
                  flowUUID: flowUUID,
                  [entity]: {
                    uuid: blockUUID,
                    key: blockOperation,
                  },
                  before: deepCopy(blockDetails.style),
                  after: deepCopy(style)
                },
              },
            });
          }
          break;
        }
        case 'link-block': {
          const { flowUUID } = details;
          let contents = details.contents;
          if (details[blockOperation]) {
            contents = details[blockOperation];
          }
          const oldContents = deepCopy(blockDetails.contents);
          const { variables } = getCreativeBlockVariableContract(
                contents,
                allVariables,
              );
          store.set({ [blockUUID]: { ...blockDetails, contents: contents, variableContract: variables } });
          if (isUserAction) {
            const entity = 'block';
            historyStore.set({
            changes: {
              entity: entity,
              operation: 'update',
              data: {
                uuid: blockUUID,
                flowUUID: flowUUID,
                [entity]: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: oldContents,
                after: contents
              },
            },
            });
          }
          break;
        }
        case 'isEditable': {
          let previousValue = blockDetails?.isEditable;
            store.set({
              [blockUUID]: Object.assign({}, blockDetails, {
                isEditable: details.isEditable
              })
            });
          if (isUserAction) {
            historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
              uuid: blockUUID,
              flowUUID: details.flowUUID,
              block: {
                uuid: blockUUID,
                key: blockOperation,
              },
              before: previousValue,
              after: details.isEditable
            }}});
          }
          break;
        }
      case 'grouping':
        const beforeDetails = deepCopy(blockDetails);        
        let newContent = details.op === 'undo' || details.op === 'redo' ? details.grouping : details.contents;
        if (typeof details.contents === 'function') {
          response = details.contents(deepCopy(blockDetails.contents));
        }
        else {
          response = details.grouping;
        }
        if (typeof details.contents === 'function') {
          newContent.contents = response;
        }
        const groupArray = getGroupsFromMap(newContent.contents,element_groups);          
        store.set({
          [blockUUID]: Object.assign({}, blockDetails, {
            contents: newContent.contents,
            groups: groupArray
          })
        });
        if (isUserAction) {
          const entity = blockDetails.type === HEADER || blockDetails.type === FOOTER ? blockDetails.type : 'block';
          historyStore.set({
            changes: {
              entity: entity,
              operation: 'update',
              data: {
                uuid: blockUUID,
                flowUUID: details.flowUUID,
                [entity]: {
                  uuid: blockUUID,
                  key: blockOperation,
                },
                before: beforeDetails,
                after: newContent
              }
            }
          })
        }
      default:
        return;
    }
  }, []);

  store.updateGridElementStyles = useCallback((blockUUID, details, isUserAction = true) => {
    const blockDetails = store.get()[blockUUID];
    const previousValue = Object.assign({}, blockDetails);
    const newValue = {
      width: details.width
    };
    if (details.height) {
      newValue.height = details.height;
    }
    const extraChanges = details.extraChanges;
    const newDetails = Object.assign({}, blockDetails, {
      style: Object.assign({}, blockDetails.style, newValue)
    }, extraChanges);
    store.set({
      [blockUUID]: newDetails
    });
    console.log("store changes :: ", store.get()[blockUUID]);
     if (isUserAction) {
       historyStore.set({ changes: { entity: 'block', operation: 'update', data: {
         uuid: blockUUID,
         flowUUID: details.flowUUID,
         block: {
           uuid: blockUUID,
           key: 'element',
         },
         before: previousValue,
         after: newDetails
       }}});
     }
    return true;
  })

  const getPositionUpdatedPageNumber = (pageSize, existingFlow, store) => {
    const pageNumber = store.get()[existingFlow.pageNumber];
    const pageNumberCopy = deepCopy(pageNumber);
    const { style } = pageNumberCopy.contents;
    const updatedStyle = { ...style };
    const contentWidth = updatedStyle.x + updatedStyle.width;
    if (existingFlow.width > pageSize.widthInPX) {
      const diff = existingFlow.width - pageSize.widthInPX;
      if (contentWidth - diff > pageSize.widthInPX) {
        updatedStyle.x = updatedStyle.x - diff;
      }
    } else if (pageSize.widthInPX > existingFlow.width) {
      const diff = pageSize.widthInPX - existingFlow.width;
      if (contentWidth + diff > existingFlow.width) {
        updatedStyle.x = updatedStyle.x + diff;
      }
    }
    pageNumberCopy.contents.style = updatedStyle;
    return pageNumberCopy;
  };

  store.setFlowSettings = useCallback((pageOperation, details, isUserAction = true) => {
    const existingFlow = store.get()[details.uuid];
    const headerHeight = store.get()[existingFlow[HEADER]]?.style?.height || 0;
    const footerHeight = store.get()[existingFlow[FOOTER]]?.style?.height || 0;
    const marginTop = existingFlow.margin[0] || 0;
    const marginBottom = existingFlow.margin[2] || 0;
    const flowElement = document.getElementById(details.uuid);

    switch(pageOperation) {
      case 'setPageMargin':
        const newMargin = [
          details.marginTop !== undefined ? details.marginTop : existingFlow.margin[0],
          details.marginRight !== undefined ? details.marginRight : existingFlow.margin[1],
          details.marginBottom !== undefined ? details.marginBottom : existingFlow.margin[2],
          details.marginLeft !== undefined ? details.marginLeft : existingFlow.margin[3]
        ];
        let newContentAreaHeight = existingFlow.height - (newMargin[0] + newMargin[2] + (headerHeight + footerHeight));
        let newContentAreaWidth = existingFlow.width - (newMargin[1] + newMargin[3]);
        if (newContentAreaWidth < 100) {
          message.warn(t(COMPLEX_PAGE_OPTIONS.WIDTH_DOES_NOT_FIT_CONTENT.key, COMPLEX_PAGE_OPTIONS.WIDTH_DOES_NOT_FIT_CONTENT.value));
          return false;
        }
        store.set({
          [details.uuid]: Object.assign({}, existingFlow, {
            margin: newMargin
          })
        });
        flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight',{detail: newContentAreaHeight}));
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
            flow: {
              uuid: details.uuid,
            },
            after: newMargin,
            before: existingFlow.margin,
            updateKey: 'margin'
          }}});
        }
        return true;
      case 'setPageBackground':
        store.set({
          [details.uuid]: Object.assign({}, existingFlow, {
            backgroundColor: details.backgroundColor
          })
        });
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
            flow: {
              uuid: details.uuid,
            },
            after: details.backgroundColor,
            before: existingFlow.backgroundColor,
            updateKey: 'backgroundColor'
          }}});
        }
        return;
      case 'setPageColor':
        store.set({
          [details.uuid]: Object.assign({}, existingFlow, {
            color: details.color
          })
        });
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
            flow: {
              uuid: details.uuid,
            },
            after: details.color,
            before: existingFlow.color,
            updateKey: 'color'
          }}});
        }
        return;
      case 'setPageSize':
        let isSize = true;
        // break;
      case 'setPageOrientation':
        const newSize = details?.size || existingFlow.size;
        const newOrientation = details?.orientation || existingFlow.orientation;
        const paperSize = paperSizes.filter(paper => paper.lowercaseName === newSize)[0];
        let pageSize =
          paperSize.lowercaseName === 'custom' 
          ? {
              widthInPX: existingFlow.width,
              heightInPX: existingFlow.height,
              widthInMM: existingFlow.widthInMM,
              heightInMM: existingFlow.heightInMM,
            }
          : paperSize ? paperSize[newOrientation] : {};
        const before = {
          size: existingFlow.size,
          orientation: existingFlow.orientation
        };
        let prevCustomSize = {};
        if(existingFlow?.size === 'custom') {
          prevCustomSize = {
            widthInPX: existingFlow.width,
            heightInPX: existingFlow.height,
            widthInMM: existingFlow.widthInMM,
            heightInMM: existingFlow.heightInMM,
          }
        } else if (details?.size === 'custom' && details?.prevCustomSize) {
          pageSize = { ...details.prevCustomSize }
        }
        const changesInStore = autoTransformStandaloneBlocks(
          { ...details, widthInPX: pageSize.widthInPX },
          existingFlow,
          store,
        );
        const storeObj = {
          [details.uuid]: Object.assign({}, existingFlow, {
            width: pageSize.widthInPX,
            height: pageSize.heightInPX,
            widthInMM: pageSize.widthInMM,
            heightInMM: pageSize.heightInMM,
            size: newSize,
            orientation: newOrientation
          }),
          ...changesInStore
        };
        if (existingFlow.pageNumber) {
          storeObj[existingFlow.pageNumber] = getPositionUpdatedPageNumber(
            pageSize,
            existingFlow,
            store,
          );
        }
        store.set(storeObj);
        newContentAreaHeight = pageSize.heightInPX - (marginTop + marginBottom + headerHeight + footerHeight);
        flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight', {detail: newContentAreaHeight}));
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
            flow: {
              uuid: details.uuid,
            },
            after: isSize ? details.size : details.orientation,
            before: isSize ? before.size : before.orientation,
            updateKey: isSize ? 'size' : 'orientation',
            prevCustomSize: {...prevCustomSize},
          }}});
          console.log("Changes in history :: ", historyStore.get());
        }
        return;
        case 'setPageHeight':
          store.set({
            [details.uuid]: Object.assign({}, existingFlow, {
              height: details.heightInPX,
              heightInMM: details.heightInMM,
              size: details.size,
              orientation: details.orientation,
            })
          });
          newContentAreaHeight = details.heightInPX - (marginTop + marginBottom + headerHeight + footerHeight);
          flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight', {detail: newContentAreaHeight}));
          if (isUserAction) {
            historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
              flow: {
                uuid: details.uuid,
              },
              after: details.heightInPX,
              before: existingFlow.height,
              updateKey: 'height'
            }}});
          }
        return;
        case 'setPageWidth': {
          const changesInStore = autoTransformStandaloneBlocks(details, existingFlow, store);
          const storeObj = {
            [details.uuid]: Object.assign({}, existingFlow, {
              width: details.widthInPX,
              widthInMM: details.widthInMM,
              size: details.size,
              orientation: details.orientation,
            }),
            ...changesInStore
          };
          if (existingFlow.pageNumber) {
            storeObj[existingFlow.pageNumber] = getPositionUpdatedPageNumber(
              details,
              existingFlow,
              store,
            );
          }
          store.set(storeObj);
          if (isUserAction) {
            historyStore.set({ changes: { entity: 'flow', operation: 'update', data: {
              flow: {
                uuid: details.uuid,
              },
              after: details.widthInPX,
              before: existingFlow.width,
              updateKey: 'width'
            }}});
          }
          return;
        }
      case 'dummy': // TODO : Needed currently to refresh the context for some child components eg. PageSettings
        store.set({});
        return;
      default:
        return;
    }
  });

  store.setPage = useCallback((pageOperation, details, isUserAction = true) => {
    const flowNumber = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].filter(flow => flow.uuid === details.uuid)[0];
    const flow = store.get()[details.uuid];
    console.log("Coming here ", pageOperation, details);
    const flowElement = document.getElementById(details.uuid);
    switch(pageOperation) {
      case 'addBlock': // Can accomodate move element as well
        const count = {
          createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
          chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
        }
        console.log("addBlock :: ", JSON.parse(JSON.stringify(details)));
        let newBlock = details.block;
        let currentFlow = details.uuid;
        let currentPageBlocks = [];
        let pageBlocks = flow[details.pageId];
        let currentFlowUUID;
        const currentPosition = {};
        let changeInPersistedKeys = {};
        let additionalChanges = {};
        if (details.currentPageId) {
          currentFlowUUID = details.currentPageId.slice(0, details.currentPageId.length - 2);
          currentFlow = store.get()[currentFlowUUID];
          currentPageBlocks = currentFlow[details.currentPageId];
          currentPosition.uuid = currentFlowUUID;
          currentPosition.pageId = details.currentPageId;
          currentPosition.currentPageId = details.pageId;
        } else {
          currentPageBlocks = pageBlocks;
        }
        const movedItemIndex = currentPageBlocks.findIndex(el => el === newBlock.uuid);
        const moveElement = movedItemIndex !== -1;

        if (details.currentPageId === details.pageId) {
          let dropIndex = pageBlocks.findIndex(el => el === details.dropItem);
          if (details.dropItem === 'last') {
            dropIndex = pageBlocks.length - 1;
          }
          if (details.dropType === 'top') {
            dropIndex -= 1; // Previous element TODO : Check if negative values affect the slice operation
          }
          if (dropIndex === movedItemIndex) {
            console.log("Trying to drop the block in the same area : do not react");
            return;
          }
        }

        if (moveElement) {
          newBlock = {
            uuid: currentPageBlocks[movedItemIndex]
          };
          if (movedItemIndex > 0) {
            currentPosition.dropItem = currentPageBlocks[movedItemIndex - 1];
            currentPosition.dropType = 'bottom';
          } else {
            if (currentPageBlocks.length > 1) {
              currentPosition.dropItem = currentPageBlocks[1];
              currentPosition.dropType = 'top';
            } else {
              currentPosition.dropItem = 'last';
              currentPosition.dropType = 'over';
            }
          }
          console.log({ movedItemIndex, currentPosition });
          // page elements cannot be empty in this case
          currentPageBlocks.splice(movedItemIndex, 1);
          if (details.currentPageId === details.pageId) { // source and target page Ids are the same
            pageBlocks = currentPageBlocks;
          }
        } else if (!newBlock.uuid) {
          newBlock.uuid = uuidv4();
          let blocksUUID = [];
          let blockMap = new Map();

          updateStoreForBlock(
            newBlock,
            newBlock,
            changeInPersistedKeys,
            blocksUUID,
            blockMap,
            additionalChanges,
            count,
            undefined,
            true
          );
        }
        // Index of the element on which drop event was captured
        let dropIndex = pageBlocks.findIndex(el => el === details.dropItem);
        if (details.dropItem === 'last') {
          dropIndex = pageBlocks.length - 1;
        }
        if (details.dropType === 'top') {
          dropIndex -= 1; // Previous element TODO : Check if negative values affect the slice operation
        }
        const newBlocks = !moveElement ? { [newBlock.uuid]: newBlock } : {};
        let changes = {
          [details.uuid]: Object.assign({}, flow, {
            [details.pageId]: pageBlocks.length === 0 ? [newBlock.uuid] : [
              ...pageBlocks.slice(0, dropIndex + 1),
              newBlock.uuid,
              ...pageBlocks.slice(dropIndex + 1)
            ]
          }),
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: Object.assign({},
            store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS], changeInPersistedKeys),
          ...additionalChanges
        };
        if (details.currentPageId !== undefined && details.currentPageId !== details.pageId) {
          changes = {
            ...changes,
            [currentFlowUUID]: Object.assign({}, store.get()[currentFlowUUID], {
              [details.currentPageId]: currentPageBlocks
            })
          };
        }
        console.log({ dropIndex, newBlock, changes, currentPosition });
        store.set({
            ...changes,
            ...newBlocks,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: count.createBlocksCount,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: count.chartElementsCount
        });
        console.log("Change in store :: ", JSON.parse(JSON.stringify(store.get())));
        if (isUserAction) {
          historyStore.set({ changes: { entity: 'block', operation: moveElement ? 'move' : 'add', data: {
            flowUUID: details.uuid,
            sourceFlowUUID: currentPosition.uuid,
            blockId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][newBlock.uuid],
            pageId: details.pageId,
            block: newBlock,
            uuid: newBlock.uuid,
            before: currentPosition || undefined,
            after: {
              uuid: details.uuid,
              pageId: details.pageId,
              dropItem: details.dropItem,
              dropType: details.dropType,
              currentPageId: details.currentPageId
            }
          }}});
        }
        if (!moveElement) {
          return newBlock.uuid;
        }
        return;
      case 'deleteBlock':
        let dropItem, dropType, dropBlock;
        console.log("deleteBlock ", JSON.parse(JSON.stringify(details)), flow[details.pageId]);
        const deleteIndex = flow[details.pageId].findIndex(pageEl => pageEl === details.block.uuid)
        const newPageBlocks = flow[details.pageId].filter(pageEl => pageEl !== details.block.uuid);
        console.log({ deleteIndex, newPageBlocks });
        dropBlock = Object.assign({}, store.get()[details.block.uuid]);
        let actionPageId = details.pageId;
        if (newPageBlocks.length > 0) {
          if (newPageBlocks[deleteIndex]) { // There is a block after the deleted block
            dropItem = newPageBlocks[deleteIndex];
            dropType = 'top';
          } else if (newPageBlocks[deleteIndex - 1]) { // There is a block after the deleted block
            dropItem = newPageBlocks[deleteIndex - 1];
            dropType = 'before';
          } else {
            console.log("should not come here!!");
          }
        } else { // There is no content in this page
          const currentPageIndex = flow.pages.findIndex(uuid => uuid === details.pageId);
          dropItem = 'last';
          dropType = 'before';
          actionPageId = currentPageIndex === 0 ? details.pageId : flow.pages[currentPageIndex - 1];
        }
        store.set({
          [details.uuid]: Object.assign({}, flow, {
            [details.pageId]: newPageBlocks
          })
        }, [details.block.uuid]);
        console.log("Change in store :: ", JSON.parse(JSON.stringify(store.get())));
        if (isUserAction) {
          const blockDropItem = details.dropItem || dropItem;
          const blockDropType = details.dropType || dropType;
          historyStore.set({ changes: { entity: 'block', operation: 'delete', data: {
            flowUUID: details.uuid,
            block: dropBlock,
            uuid: dropBlock.uuid,
            pageId: actionPageId,
            deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][dropBlock.uuid],
            before: {
              dropItem: blockDropItem,
              dropType: blockDropType,
            },
            after: {},
          }}});
        }
        return;
      case 'addStaticElement':
        if (flow[details.type] !== undefined) {
          message.warn(t(COMPLEX_PAGE_OPTIONS.HEADER_OR_FOOTER_ALREADY_EXISTS.key, COMPLEX_PAGE_OPTIONS.HEADER_OR_FOOTER_ALREADY_EXISTS.value, { type: details.type }));
          return;
        }
        const staticElement = JSON.parse(JSON.stringify(details.block));
        if (!staticElement.uuid) {
          staticElement.type = details.type;
          staticElement.uuid = uuidv4();
        }
        const otherStaticElementType = details.type === HEADER ? FOOTER : HEADER;
        let totalHeaderFooterHeight = (details.block.style.height || 0) + (store.get()[flow[otherStaticElementType]]?.style?.height || 0)
        if(flow.height < totalHeaderFooterHeight) {
          message.warn(
            t(
              COMPLEX_PAGE_OPTIONS.HEADER_FOOTER_ALREADY_MAX_HEIGHT.key,
              COMPLEX_PAGE_OPTIONS.HEADER_FOOTER_ALREADY_MAX_HEIGHT.value,
              { otherElement: otherStaticElementType, dropElement: details.type },
            ),
            6,
          );
          return false;
        }
        if(staticElement.contents.length>0){
          const groupIdsArray = [];
          const groupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
          const isClone = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED];
          staticElement.contents.forEach(content =>{
            if (isClone) {
              const existingGroupId = content?.groupId;
              const newGroupId = uuidv4();
              if (existingGroupId && !groupIdsArray.some(item => item.existingGroupId === existingGroupId)) {
                groupIdsArray.push({ existingGroupId: existingGroupId, newGroupId: newGroupId })
              }
              const newGroupIdfromArray = groupIdsArray.find(i => i.existingGroupId === existingGroupId)?.newGroupId ?? null;
              if (existingGroupId) {
                const group_id = newGroupIdfromArray ? newGroupIdfromArray : newGroupId;
                groupMap.set(group_id, { groupName: '', isBLPresent: false });
                content.groupId = group_id;
              }
            }
            if (!content.uuid) {
              content.uuid = uuidv4();
              if (content.contentType === STATIC_TABLE_ELEMENT) {
                content.contents = content.contents.map(cell => {
                  cell.uuid = uuidv4();
                  return cell;
                });
              }
            }
          })
          if(isClone){
            const groupArray = getGroupsFromMap(staticElement.contents, groupMap);
            staticElement.groups = groupArray;
          }
        }
        if (staticElement?.isStandalone) {
          store.set({
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: Object.assign(
              {},
              store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS],
              { [staticElement.uuid]: staticElement.blockId },
            ),
          });
        }
        store.set({
          [details.uuid]: Object.assign({}, flow, {
            [details.type]: staticElement.uuid
          }),
          [staticElement.uuid]: staticElement
        });
        let newContentAreaHeight = flow.height - (flow.margin[0] + flow.margin[2] + totalHeaderFooterHeight);
        flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight',{detail:{newContentAreaHeight}}));
        console.log("Changes in store :: ", store.get(), staticElement);
        if (isUserAction) {
          historyStore.set({ changes: { entity: details.type, operation: 'add', data: {
            flowUUID: details.uuid,
            [details.type]: staticElement,
            uuid: staticElement.uuid
          }}});
        }
        return;
      case 'preview-block':
        console.log("preview block added for text overflow :: ", details);
        const { itemUUID, flowUUID, overflowPageId, overflowBlock, contentSplitIndex, textSplitIndex  } = details;
        const flowDetails = store.get()[flowUUID];
        const blockDetails = store.get()[itemUUID]; // TODO : Reduce the content of source block
        if (contentSplitIndex !== undefined) {
          let secondBlockSplitIndex = contentSplitIndex;
          // Include the overflow block in the overflow page
          let firstHalfContents = [], secondHalfContents = [];
          let newLineContent = -1;
          if (blockDetails.contents[contentSplitIndex].contentType !== 'LINE_BREAK') {
            newLineContent = blockDetails.contents.findIndex((content, contentIndex) => {
              return contentIndex > contentSplitIndex && content.contentType === 'LINE_BREAK'
            });
          }
          if (textSplitIndex !== undefined) {
            if (blockDetails.contents[contentSplitIndex].content.slice(textSplitIndex).length > 0) {
              secondHalfContents.push(Object.assign({}, blockDetails.contents[contentSplitIndex], {
                content: blockDetails.contents[contentSplitIndex].content.slice(textSplitIndex)
              }));
            } else {
              secondBlockSplitIndex = newLineContent !== -1 ? newLineContent : contentSplitIndex;
            }
            firstHalfContents.push(Object.assign({}, blockDetails.contents[contentSplitIndex], {
              content: blockDetails.contents[contentSplitIndex].content.slice(0, textSplitIndex)
            }));
            if (newLineContent !== -1) {
              firstHalfContents.push(Object.assign({}, blockDetails.contents[newLineContent]));
            }
          }
          const newBlock = Object.assign({}, overflowBlock, {
            contents: [...secondHalfContents, ...blockDetails.contents.slice(secondBlockSplitIndex + (textSplitIndex !== undefined ? 1 : 0))],
            style: Object.assign({}, overflowBlock.style, {
              padding: Object.assign({}, overflowBlock.style.padding, {
                top: 0
              })
            })
          });
          const oldBlock = Object.assign({}, blockDetails, {
            contents: [...blockDetails.contents.slice(0, contentSplitIndex), ...firstHalfContents]
          });
          store.set({
            [flowUUID]: Object.assign({}, flowDetails, {
              [overflowPageId]: [overflowBlock.uuid, ...flowDetails[overflowPageId]],
              [`limit-${overflowPageId}`]: flowDetails[[`limit-${overflowPageId}`]] + 1
            }),
            [overflowBlock.uuid]: newBlock,
            [itemUUID]: oldBlock
          });
          console.log({ newBlock, oldBlock, blockDetails, newLineContent });
        } else {
          // Check if overflowPageID has other blocks
          // Else remove the overflow block itself
          if (flowDetails[overflowPageId].length === 0) {
            console.log("Removing the overflow page!!!");
            const newDetails = Object.assign({}, flowDetails, {
              pages: flowDetails.pages.filter(page => page !== overflowPageId)
            });
            delete newDetails[overflowPageId];
            store.set({
              [flowUUID]: newDetails
            });
          }
        }
        return;
      case 'deleteStaticElement':
        const blockUUID = flow[details.type];
        const existingElement = store.get()[blockUUID];
        store.set({ [details.uuid]: Object.assign({}, flow, { [details.type] : undefined }) }, []); // we are not passing any blockUUID in the array, due to rendering issue when any promise is handled before deletion.
        const otherStaticElementHeight = details.type === HEADER ? store.get()[flow[FOOTER]]?.style.height : store.get()[flow[HEADER]]?.style.height;
        newContentAreaHeight = flow.height - (flow.margin[0] + flow.margin[2] + (otherStaticElementHeight || 0));
        flowElement && flowElement.dispatchEvent(new CustomEvent('checkCreativeBlockHeight', {detail :{newContentAreaHeight}}));
        if (isUserAction) {
          historyStore.set({ changes: { entity: details.type, operation: 'delete', data: {
            flowUUID: details.uuid,
            [details.type]: existingElement,
            uuid: existingElement.uuid,
            deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockUUID]
          }}});
        }
        return;
      case 'addMultipleBlocks':
        let drop_Index;
        let page_Blocks = flow[details.pageId];
        const changesInStore = {};
        const persistedKeys = {};
        const blocksUUID = [];
        const blockMap = new Map();
        const elementCount = {
          createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
          chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
        }

        const isClone= store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED];         

        details.blocks.forEach((block, index) => {
          const newBlock = JSON.parse(JSON.stringify(block));
          newBlock.uuid = uuidv4();
          if (!details.isEmailTemplate) {
            newBlock.style = { ...newBlock.style, ...details.style };
          }
          const currentPosition = {};
          // let additionalChanges = {};
          const groupMap = new Map(); 
          updateStoreForBlock(
            block,
            newBlock,
            persistedKeys,
            blocksUUID,
            blockMap,
            changesInStore,
            elementCount,
            undefined,
            true,
            false,
            false,
            groupMap,
            isClone,
          );
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]: false })
          const existingGroupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
          const mergedGroup = new Map([...existingGroupMap, ...groupMap]);
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: mergedGroup })

          // Index of the element on which drop event was captured
          if(index === 0){
            drop_Index = page_Blocks.findIndex(el => el === details.dropItem);
            if (details.dropItem === 'last') {
              drop_Index = page_Blocks.length - 1;
            }
            if (details.dropType === 'top') {
              drop_Index -= 1; // Previous element TODO : Check if negative values affect the slice operation
            }
            drop_Index = drop_Index + 1;
            page_Blocks = [
              ...page_Blocks.slice(0, drop_Index ),
              newBlock.uuid,
              ...page_Blocks.slice(drop_Index)
            ]
          }else{
            page_Blocks =[
              ...page_Blocks.slice(0, drop_Index ),
              newBlock.uuid,
              ...page_Blocks.slice(drop_Index)
            ]
          }

          const changes = {
            [details.uuid]: Object.assign({}, flow, {
              [details.pageId]: page_Blocks.length === 0 ? [newBlock.uuid] : page_Blocks
            }),
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: Object.assign({},
              store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS], persistedKeys),
            ...changesInStore,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: elementCount.createBlocksCount,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: elementCount.chartElementsCount,
          };

          store.set({
            ...changes,
          });

          if (isUserAction) {
            historyStore.set({ changes: { entity: 'block', operation: 'add', data: {
              flowUUID: details.uuid,
              sourceFlowUUID: currentPosition.uuid,
              blockId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][newBlock.uuid],
              pageId: details.pageId,
              block: newBlock,
              uuid: newBlock.uuid,
              before: currentPosition || undefined,
              after: {
                uuid: details.uuid,
                pageId: details.pageId,
                dropItem: index>0 ? page_Blocks[drop_Index-1] : details.dropItem,
                dropType: index>0 ? 'bottom' : details.dropType,
                currentPageId: details.currentPageId
              }
            }}});
          }
          drop_Index++;
        });
        return;
      case 'deleteMultipleBlocks':
        let _deleteIndex;
        let _newPageBlocks = deepCopy(flow[details.pageId]);
        details.blocks.forEach(blockUUID =>{
          let dropItem, dropType, dropBlock;
          _deleteIndex = _newPageBlocks.findIndex(pageEl => pageEl === blockUUID)
          _newPageBlocks = _newPageBlocks.filter(pageEl => pageEl !== blockUUID);
          dropBlock = Object.assign({}, store.get()[blockUUID]);
          let actionPageId = details.pageId;
          if (_newPageBlocks.length > 0) {
            if (_newPageBlocks[_deleteIndex]) { 
              dropItem = _newPageBlocks[_deleteIndex];
              dropType = 'top';
            } else if (_newPageBlocks[_deleteIndex - 1]) { 
              dropItem = _newPageBlocks[_deleteIndex - 1];
              dropType = 'before';
            } else {
              console.log("should not come here!!");
            }
          } else { 
            const currentPageIndex = flow.pages.findIndex(uuid => uuid === details.pageId);
            dropItem = 'last';
            dropType = 'before';
            actionPageId = currentPageIndex === 0 ? details.pageId : flow.pages[currentPageIndex - 1];
          }
          if (isUserAction) {
            const blockDropItem = details.dropItem || dropItem;
            const blockDropType = details.dropType || dropType;
            historyStore.set({ changes: { entity: 'block', operation: 'delete', data: {
              flowUUID: details.uuid,
              block: dropBlock,
              uuid: dropBlock.uuid,
              pageId: actionPageId,
              deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][dropBlock.uuid],
              before: {
                dropItem: blockDropItem,
                dropType: blockDropType,
              },
              after: {},
            }}});
          }
        })
        store.set({
          [details.uuid]: Object.assign({}, flow, {
            [details.pageId]: _newPageBlocks
          })
        }, []); // we are not passing any blockUUID in the array, due to rendering issue when any promise is handled before deletion.
        return;
      case 'replaceBlocks': {
        const { blocksIdsMap } = details;
        const pageId = `${details.uuid}-0`;
        const oldBlockUUIDs = flow[pageId];
        const oldBlocks = oldBlockUUIDs.map(uuid => store.get()[uuid]);
        const newBlockUUIDs = [];
        const changesInStore = {};
        const persistedKeys = {};
        const blockMap = new Map();
        const count = {
          createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
          chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
        }
        details.blocks.map(block => {
          if (blocksIdsMap) {
            const newUUID = uuidv4();
            blocksIdsMap.set(block.uuid, newUUID);
            block.uuid = newUUID;
          }
          block.type = block.blockType;
          if (block.blockType !== STANDALONE_BLOCK) {
            delete block.blockId;
          }
          delete block.orderNumber;
          updateStoreForBlock(
            block,
            block,
            persistedKeys,
            newBlockUUIDs,
            blockMap,
            changesInStore,
            count,
          );
          return block;
        });
        const oldStyle = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES];
        const oldFlowStyle = {};
        Object.keys(details.flowStyle).forEach(key => {
          oldFlowStyle[key] = flow[key] ? flow[key] : '';
        });
        store.set(
          {
            [details.uuid]: Object.assign({}, flow, {
              [pageId]: newBlockUUIDs,
              ...details.flowStyle,
            }),
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: details.style,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: Object.assign({},
              store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS], persistedKeys),
            ...changesInStore,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: count.createBlocksCount,
            [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: count.chartElementsCount,
          },
          // [oldBlockUUIDs], // we are not passing any blockUUID in the array, due to rendering issue when any promise is handled before deletion.
        );

        if (isUserAction) {
          historyStore.set({
            changes: {
              entity: 'block',
              operation: 'replace',
              data: {
                flowUUID: details.uuid,
                uuid: details.uuid,
                pageId: pageId,
                before: {
                  uuid: details.uuid,
                  blocks: oldBlocks,
                  flowStyle: oldFlowStyle,
                  style: oldStyle,
                  deleteIds: newBlockUUIDs,
                },
                after: {
                  uuid: details.uuid,
                  blocks: details.blocks,
                  flowStyle: details.flowStyle,
                  style: details.style,
                  deleteIds: oldBlockUUIDs,
                },
              },
            },
          });
        }
        return;
      }
      case 'dummy': // TODO : Needed currently to refresh the context for some child components eg. PageSettings
        store.set({});
        return;
      default:
        return;
    }
  }, []);

  store.addPageNumber = useCallback((details, isUserAction = true) => {
    const currentStore = store.get();
    const existingFlows = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
    const storeObj = {};
    const pageNumber = deepCopy(details.block);
    delete pageNumber.contents.contents;
    delete pageNumber.pageNumberType;
    existingFlows.forEach(flowId => {
      const flow = currentStore[flowId];
      const pageNumberUUID = flow[PAGE_NUMBER];
      storeObj[pageNumberUUID] = pageNumber;
    });
    const documentStyles = deepCopy(currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]);
    const docPageNumber = deepCopy(details.block);
    documentStyles[PAGE_NUMBER] = docPageNumber;
    storeObj[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES] = { ...documentStyles };
    store.set(storeObj);
    if (isUserAction) {
      historyStore.set({
        changes: {
          entity: PAGE_NUMBER,
          operation: 'add',
          data: {
            flowUUID: details.uuid,
            [PAGE_NUMBER]: docPageNumber,
          },
        },
      });
    }
  }, []);

  store.deletePageNumber = useCallback((details, isUserAction = true) => {
    const currentStore = store.get();
    const documentStyles = deepCopy(currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]);
    const existingPageNumberElement = documentStyles[PAGE_NUMBER];
    delete documentStyles[PAGE_NUMBER];

    store.set({
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: { ...documentStyles },
    });
    if (isUserAction) {
      historyStore.set({
        changes: {
          entity: PAGE_NUMBER,
          operation: 'delete',
          data: {
            flowUUID: details.uuid,
            [PAGE_NUMBER]: existingPageNumberElement,
          },
        },
      });
    }
  }, []);

  store.updatePageNumber = useCallback((details, isUserAction = true) => {
    const { uuid, contents, style, pageNumberType, historyChange } = details;
    const currentStore = store.get();
    const flow = currentStore[uuid];
    const pageNumberUUID = flow[PAGE_NUMBER];
    const documentStyles = deepCopy(currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]);
    const originalElement = documentStyles[PAGE_NUMBER];
    const docPageNumber = deepCopy(originalElement);
    if (contents) {
      docPageNumber.contents.contents = contents;
    }
    if (style) {
      if (style.width && typeof style.width === 'string') {
        style.width = parseInt(style.width.replaceAll('px', ''));
      }
      if (style.height && typeof style.height === 'string') {
        style.height = parseInt(style.height.replaceAll('px', ''));
      }
      docPageNumber.contents.style = { ...docPageNumber.contents.style, ...style };
    }
    if (pageNumberType) {
      docPageNumber.pageNumberType = pageNumberType;
    }
    documentStyles[PAGE_NUMBER] = docPageNumber;
    const pageNumber = deepCopy(docPageNumber);
    delete pageNumber.pageNumberType;
    delete pageNumber.contents.contents;

    store.set({
      [pageNumberUUID]: { ...pageNumber },
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: { ...documentStyles },
    });
    if (isUserAction) {
      historyStore.set({
        changes: {
          entity: 'flow',
          operation: 'update',
          data: {
            flow: {
              uuid: uuid,
            },
            flowUUID: uuid,
            before: originalElement,
            after: docPageNumber,
            updateKey: PAGE_NUMBER,
          },
          undoLength: historyChange.undo,
          redoLength: historyChange.redo,
          textOperation: historyChange.op,
        },
      });
    }
  }, []);

  store.updatefontFamilyAndSize = useCallback((CurrentDesign, newFontFamilyOrSize, oldFontFamilyorSize, styleType, operation, isUserAction=true)=>{
    defautlStyleStore.set({[styleType]: newFontFamilyOrSize});
    if (CurrentDesign === 'flow' || CurrentDesign === 'block') {
      const flowId = store.get().flows[0];
      const flow = deepCopy(store.get()[flowId]);
      if (isUserAction) {
        historyStore.set({ changes: { entity: CurrentDesign, operation: 'update', data: {
          ...(CurrentDesign==='flow' && {
            [CurrentDesign]:{
              uuid: flow.uuid,
              style: {
                ...(styleType && newFontFamilyOrSize && {[styleType] : newFontFamilyOrSize }),
              },
            },
          }),
          ...(CurrentDesign==='block' && { uuid: flow.uuid, flowUUID: flow.uuid,
            [CurrentDesign]:{
              uuid: flow.uuid,
              style: {
                ...(styleType && newFontFamilyOrSize && {[styleType] : newFontFamilyOrSize }),
              },
            },
          }),
          after: newFontFamilyOrSize,
          before: oldFontFamilyorSize,
          updateKey: styleType
        }}});
      }
    } else {
      if (isUserAction) {
        historyStore.set({ changes: { entity: CurrentDesign, operation: 'update', data: {
          style: {
            ...(styleType && newFontFamilyOrSize && {[styleType] : newFontFamilyOrSize }),
          },
          after: newFontFamilyOrSize,
          before: oldFontFamilyorSize,
          updateKey: styleType
        }}});
      }
    }
    document.dispatchEvent(
      new CustomEvent('textElementFontStyleUpdate', { detail: {newFontFamilyOrSize, styleType, action: operation } })
    )
  },[]);

  store.updateSummary = useCallback((summaryContents, newGlobalVariables, isUserAction = true) => {
    const { variables } = summaryContents ? getCreativeBlockVariableContract(summaryContents, newGlobalVariables) : { variables: [] };
    const existingSummary = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY];
    store.set({
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY]: Object.assign({}, existingSummary, {
        contents: summaryContents || [],
        variableContract: variables
      })
    });
    if (isUserAction) {
      historyStore.set({ changes: { entity: 'documentSummary', operation: 'update', data: {
        summary: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY].contents,
        after: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY].contents,
        before: existingSummary?.contents
      }}});
    }
  }, []);

  store.applyReverseChanges = useCallback((applyAction) => {
    if(applyAction.entity === 'document'){
      if (applyAction.operation === 'update') {
        const styleType = applyAction.data.updateKey
        const newFontFamilyOrSize = applyAction.data.before;
        const oldFontFamilyorSize = applyAction.data.after;
        store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'undo', false );
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            style: {
              ...(styleType && newFontFamilyOrSize && {[styleType] : newFontFamilyOrSize }),
            },
            updateKey: styleType,
            value: newFontFamilyOrSize,
          },
        };
      }
    } else if (applyAction.entity === 'documentSummary') {
      if (applyAction.operation === 'update') {
        store.updateSummary(applyAction.data.before, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], false);
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            summary: applyAction.data.before
          },
        }
      }
    } else if (applyAction.entity === 'flow') {
      if (applyAction.operation === 'add') {
        store.deleteFlow(applyAction.data[applyAction.entity].uuid, false);
        return { entity: applyAction.entity, operation: 'delete', data: Object.assign({}, applyAction.data, {
          deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][applyAction.data[applyAction.entity].uuid],
          [applyAction.entity]: {
            uuid: applyAction.data[applyAction.entity].uuid
          }
        }) };
      } else if (applyAction.operation === 'delete') {
        store.addNewFlow(applyAction.data.before, applyAction.data[applyAction.entity], false);
        return { entity: applyAction.entity, operation: 'add', data: {
          ...applyAction.data,
          uuid: applyAction.data.before,
          [applyAction.entity]: {
            uuid: applyAction.data[applyAction.entity].uuid
          }} };
      } else if (applyAction.operation === 'update') {
        if (applyAction.data.updateKey === 'margin') {
          const applyMargin = applyAction.data.before;
          store.setFlowSettings('setPageMargin', { uuid: applyAction.data[applyAction.entity].uuid, marginTop: applyMargin[0], marginRight: applyMargin[1],
            marginBottom: applyMargin[2], marginLeft: applyMargin[3] }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'margin',
              value: applyMargin
          }};
        } else if (applyAction.data.updateKey === 'backgroundColor') {
          store.setFlowSettings('setPageBackground', { uuid: applyAction.data[applyAction.entity].uuid, backgroundColor: applyAction.data.before }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'backgroundColor',
              value: applyAction.data.before
          }};
        } else if (applyAction.data.updateKey === 'size') {
          store.setFlowSettings('setPageSize', 
          { 
            uuid: applyAction.data[applyAction.entity].uuid, 
            size: applyAction.data.before,
            prevCustomSize: { ...applyAction.data.prevCustomSize }
          }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'size',
              value: applyAction.data.before
          }};
        } else if (applyAction.data.updateKey === 'orientation') {
          store.setFlowSettings('setPageOrientation', { uuid: applyAction.data[applyAction.entity].uuid, orientation: applyAction.data.before }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'orientation',
              value: applyAction.data.before
          }};
        } else if (applyAction.data.updateKey === 'height') {
          const applyHeight = applyAction.data.before;
          store.setFlowSettings('setPageHeight', {
            uuid: applyAction.data[applyAction.entity].uuid,
            size: 'custom',
            heightInPX: applyHeight,
            heightInMM: measurementConversion(applyHeight),
            orientation: 'portrait',
          }, false);
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'height',
              value: applyHeight,
            },
          };
        } else if (applyAction.data.updateKey === 'width') {
          const applyWidth = applyAction.data.before;
          store.setFlowSettings('setPageWidth', {
            uuid: applyAction.data[applyAction.entity].uuid,
            size: 'custom',
            widthInPX: applyWidth,
            widthInMM: measurementConversion(applyWidth),
            orientation: 'portrait',
          }, false);
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'width',
              value: applyWidth,
            },
          };
        } else if (applyAction.data.updateKey === PAGE_NUMBER) {
          store.updatePageNumber(
            {
              uuid: applyAction.data.flowUUID,
              contents: applyAction.data.before.contents.contents,
              style: applyAction.data.before.contents.style,
              pageNumberType: applyAction.data.before.pageNumberType,
              op: 'undo',
            },
            false,
          );
          const newData = deepCopy(applyAction.data);
          newData.value = applyAction.data.before;
          newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
          return {
            ...applyAction,
            data: {
              flow: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              flowUUID: applyAction.data[applyAction.entity].uuid,
              updateKey: applyAction.data['updateKey'],
            },
          };
        }else if (applyAction.data.updateKey === 'fontSize' || applyAction.data.updateKey === 'fontFamily') {
          const styleType = applyAction.data.updateKey
          const newFontFamilyOrSize = applyAction.data.before;
          const oldFontFamilyorSize = applyAction.data.after;
          store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'undo', false );
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: styleType,
              value: newFontFamilyOrSize,
            },
          };
        } else if (applyAction.data.updateKey === 'color') {
          store.setFlowSettings('setPageColor', { uuid: applyAction.data[applyAction.entity].uuid, backgroundColor: applyAction.data.before }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'color',
              value: applyAction.data.before
          }};
        }
      } else if (applyAction.operation === 'move') {
        store.swapFlows(applyAction.data[applyAction.entity].uuid, {
          currentPosition: applyAction.data.after,
          targetPosition: applyAction.data.before
        }, false);
        return {
          ...applyAction,
          data: Object.assign({}, applyAction.data, {
            value: applyAction.data.before
          })
        };
      } else if(applyAction.operation === 'replace') {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: applyAction.data.before
        })
        const backendIds = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS];
        const deleteIds = Array.isArray(applyAction.data.flow.uuid) ? applyAction.data.flow.uuid.map(key => backendIds[key]) : backendIds[applyAction.data.flow.uuid];
        return {
          ...applyAction,
          data: { flow: { uuid: applyAction.data.oldFlowUUID },
          deleteId: deleteIds },
        }
      }
    } else if (applyAction.entity === 'block') {
      if (applyAction.operation === 'add') {
        store.setPage('deleteBlock', { ...applyAction.data, uuid: applyAction.data.flowUUID }, false);
        return {
          entity: applyAction.entity,
          operation: 'delete',
          data: {
            flowUUID: applyAction.data.flowUUID,
            pageId: applyAction.data.pageId,
            uuid: applyAction.data[applyAction.entity].uuid,
            deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][applyAction.data[applyAction.entity].uuid],
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
              isStandalone: applyAction.data[applyAction.entity]?.isStandalone || false
            }
          }
        };
      } else if (applyAction.operation === 'move') {
        const changes = { ...applyAction.data, ...applyAction.data.before };
        store.setPage('addBlock', { ...changes }, false);
        return {
          entity: applyAction.entity,
          operation: applyAction.operation,
          data: {
            flowUUID: applyAction.data.before.uuid,
            sourceFlowUUID: applyAction.data.flowUUID,
            pageId: applyAction.data.pageId,
            uuid: applyAction.data[applyAction.entity].uuid,
            blockId: applyAction.data.blockId,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
            }
          }
        };
      } else if (applyAction.operation === 'delete') {
        const changes = { ...applyAction.data, ...applyAction.data.before };
        store.setPage('addBlock', { ...changes, uuid: applyAction.data.flowUUID }, false);
        return {
          entity: applyAction.entity,
          operation: 'add',
          data: {
            flowUUID: applyAction.data.flowUUID,
            pageId: applyAction.data.pageId,
            uuid: applyAction.data[applyAction.entity].uuid,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
              isStandalone: applyAction.data[applyAction.entity]?.isStandalone || false
            }
          }
        };
      } else if (applyAction.operation === 'replace') {
        const changes = { ...applyAction.data, ...applyAction.data.before };
        store.setPage('replaceBlocks', { ...changes, uuid: applyAction.data.flowUUID }, false);
        return {
          entity: applyAction.entity,
          operation: 'replace',
          data: {
            flowUUID: applyAction.data.flowUUID,
            pageId: applyAction.data.pageId,
            uuid: applyAction.data.flowUUID,
            value: applyAction.data.before,
          },
        };
      } else if (applyAction.operation === 'update') {
        if (applyAction.data.updateKey === 'fontSize' || applyAction.data.updateKey === 'fontFamily') {
          const styleType = applyAction.data.updateKey
          const newFontFamilyOrSize = applyAction.data.before;
          const oldFontFamilyorSize = applyAction.data.after;
          store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'undo', false );
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: styleType,
              value: newFontFamilyOrSize,
            },
          };
        }
        const entityChanges = applyAction.data[applyAction.entity];
        store.updateBlock(
          entityChanges.key,
          entityChanges.uuid,
          {
            [entityChanges.key]: applyAction.data.before,
            flowUUID: applyAction.data.flowUUID,
            op: 'undo',
            callback: applyAction.data.callback,
            tableChanges: applyAction.data.tableChanges
          },
          false
        );
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            uuid: entityChanges.uuid,
            flowUUID: applyAction.data.flowUUID,
            [applyAction.entity]: {
                uuid: entityChanges.uuid
            },
            value: applyAction.data.before
          }
        };
      }
    } else if (applyAction.entity === 'header' || applyAction.entity === 'footer') {
      if (applyAction.operation === 'add') {
        store.setPage('deleteStaticElement', { uuid: applyAction.data.flowUUID, type: applyAction.entity }, false);
        return {
          entity: applyAction.entity,
          operation: 'delete',
          data: {
            flowUUID: applyAction.data.flowUUID,
            uuid: applyAction.data[applyAction.entity].uuid,
            deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][applyAction.data[applyAction.entity].uuid],
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
              isStandalone: applyAction.data[applyAction.entity]?.isStandalone || false
            }
          }
        }
      } else if (applyAction.operation === 'delete') {
        store.setPage('addStaticElement', { uuid: applyAction.data.flowUUID, type: applyAction.entity, block: applyAction.data[applyAction.entity] }, false);
        return {
          entity: applyAction.entity,
          operation: 'add',
          data: {
            flowUUID: applyAction.data.flowUUID,
            uuid: applyAction.data[applyAction.entity].uuid,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
              isStandalone: applyAction.data[applyAction.entity]?.isStandalone || false,
            },
          },
        };
      } else if (applyAction.operation === 'update') {
        const entityChanges = applyAction.data[applyAction.entity];
        store.updateBlock(
          entityChanges.key,
          entityChanges.uuid,
          {
            [entityChanges.key]: applyAction.data.before,
            flowUUID: applyAction.data.flowUUID,
            op: 'undo',
            callback: applyAction.data.callback,
            tableChanges: applyAction.data.tableChanges
          },
          false
        );
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            uuid: entityChanges.uuid,
            flowUUID: applyAction.data.flowUUID,
            [applyAction.entity]: {
                uuid: entityChanges.uuid
            },
            value: applyAction.data.before
          }
        };
      }
    } else if (applyAction.entity === PAGE_NUMBER) {
      if (applyAction.operation === 'add') {
        store.deletePageNumber(
          {
            uuid: applyAction.data.flowUUID,
            type: applyAction.entity,
            block: applyAction.data.pageNumber,
          },
          false,
        );
        return {
          entity: applyAction.entity,
          operation: 'delete',
          data: {
            flowUUID: applyAction.data.flowUUID,
            uuid: applyAction.data[applyAction.entity].uuid,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
            },
          },
        };
      } else if (applyAction.operation === 'delete') {
        store.addPageNumber(
          {
            uuid: applyAction.data.flowUUID,
            type: applyAction.entity,
            block: applyAction.data[applyAction.entity],
          },
          false,
        );
        return {
          entity: applyAction.entity,
          operation: 'add',
          data: {
            flowUUID: applyAction.data.flowUUID,
            uuid: applyAction.data[applyAction.entity].uuid,
            [applyAction.entity]: { uuid: applyAction.data[applyAction.entity].uuid },
          },
        };
      }
    } else {
      // no-op
    }
  }, []);

  store.applyChanges = useCallback((applyAction) => {
    if(applyAction.entity === 'document'){
      if (applyAction.operation === 'update') {
        const styleType = applyAction.data.updateKey
        const newFontFamilyOrSize = applyAction.data.after;
        const oldFontFamilyorSize = applyAction.data.before;
        store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'redo', false );
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            style: {
              ...(styleType && newFontFamilyOrSize && {[styleType] : newFontFamilyOrSize }),
            },
            updateKey: styleType,
            value: newFontFamilyOrSize,
          },
        };
      }
    } else if (applyAction.entity === 'documentSummary') {
      if (applyAction.operation === 'update') {
        store.updateSummary(applyAction.data.after, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], false);
        return {
          entity: applyAction.entity,
          operation: 'update',
          data: {
            summary: applyAction.data.after
          },
        }
      }
    } else if (applyAction.entity === 'flow') {
      if (applyAction.operation === 'add') {
        if(applyAction.data.flow.flowName === TABLE_OF_CONTENTS){
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: () =>reloadTableOfContents(store)});
        }
        store.addNewFlow(applyAction.data.uuid, applyAction.data[applyAction.entity], false);
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid
            }
          }
        };
      } else if (applyAction.operation === 'delete') {
        store.deleteFlow(applyAction.data[applyAction.entity].uuid, false);
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid
            }
          }
        };
      } else if (applyAction.operation === 'update') {
        if (applyAction.data.updateKey === 'margin') {
          const applyMargin = applyAction.data.after;
          store.setFlowSettings('setPageMargin', { uuid: applyAction.data[applyAction.entity].uuid, marginTop: applyMargin[0], marginRight: applyMargin[1],
            marginBottom: applyMargin[2], marginLeft: applyMargin[3] }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'margin',
              value: applyAction.data.after
          }};
        } else if (applyAction.data.updateKey === 'backgroundColor') {
          store.setFlowSettings('setPageBackground', { uuid: applyAction.data[applyAction.entity].uuid, backgroundColor: applyAction.data.after }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'backgroundColor',
              value: applyAction.data.after
          }};
        } else if (applyAction.data.updateKey === 'size') {
          store.setFlowSettings('setPageSize', { uuid: applyAction.data[applyAction.entity].uuid, size: applyAction.data.after }, false);
          return { entity: applyAction.entity, operation: 'update', data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'size',
              value: applyAction.data.after
          }};
        } else if (applyAction.data.updateKey === 'orientation') {
           store.setFlowSettings('setPageOrientation', { uuid: applyAction.data[applyAction.entity].uuid, orientation: applyAction.data.after }, false);
           return { entity: applyAction.entity, operation: 'update', data: {
               [applyAction.entity]: {
                 uuid: applyAction.data[applyAction.entity].uuid,
               },
               updateKey: 'orientation',
               value: applyAction.data.after
           }};
         } else if (applyAction.data.updateKey === 'height') {
          const applyHeight = applyAction.data.after;
          store.setFlowSettings('setPageHeight', {
            uuid: applyAction.data[applyAction.entity].uuid,
            size: 'custom',
            heightInPX: applyHeight,
            heightInMM: measurementConversion(applyHeight),
            orientation: 'portrait',
          }, false);
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'height',
              value: applyHeight,
            },
          };
        } else if (applyAction.data.updateKey === 'width') {
          const applyWidth = applyAction.data.after;
          store.setFlowSettings('setPageWidth', {
            uuid: applyAction.data[applyAction.entity].uuid,
            size: 'custom',
            widthInPX: applyWidth,
            widthInMM: measurementConversion(applyWidth),
            orientation: 'portrait',
          }, false);
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: 'width',
              value: applyWidth,
            },
          };
        } else if (applyAction.data.updateKey === PAGE_NUMBER) {
          store.updatePageNumber(
            {
              uuid: applyAction.data.flowUUID,
              contents: applyAction.data.after.contents.contents,
              style: applyAction.data.after.contents.style,
              pageNumberType: applyAction.data.after.pageNumberType,
              op: 'redo',
            },
            false,
          );
          const newData = deepCopy(applyAction.data);
          newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
          newData.value = applyAction.data.after;
          return {
            ...applyAction,
            data: {
              flow: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              flowUUID: applyAction.data[applyAction.entity].uuid,
              updateKey: applyAction.data['updateKey'],
            },
            op: 'redo',
          };
        } else if (applyAction.data.updateKey === 'fontSize' || applyAction.data.updateKey === 'fontFamily') {
          const styleType = applyAction.data.updateKey
          const newFontFamilyOrSize = applyAction.data.after;
          const oldFontFamilyorSize = applyAction.data.before;
          store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'redo', false );
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: styleType,
              value: newFontFamilyOrSize,
            },
          };
        }
      } else if (applyAction.operation === 'move') {
        store.swapFlows(applyAction.data[applyAction.entity].uuid, {
          currentPosition: applyAction.data.before,
          targetPosition: applyAction.data.after
        }, false);
        return {
          ...applyAction,
          data: Object.assign({}, applyAction.data, {
            value: applyAction.data.after
          })
        };
      } else if(applyAction.operation === 'replace') {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: applyAction.data.after
        })
        const backendIds = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS];
        const deleteIds = Array.isArray(applyAction.data.oldFlowUUID) ? applyAction.data.oldFlowUUID.map(key => backendIds[key]) : backendIds[applyAction.data.oldFlowUUID];
        return {
          ...applyAction,
          data: { flow: { uuid: applyAction.data.flow.uuid },
          deleteId: deleteIds }
        }
      }
    } else if (applyAction.entity === 'block') {
      if (applyAction.operation === 'add') {
        store.setPage('addBlock', { ...applyAction.data, ...applyAction.data.after, uuid: applyAction.data.flowUUID }, false);
        const newData = JSON.parse(JSON.stringify(applyAction.data));
        newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
        delete newData.before;
        delete newData.after;
        newData.value = applyAction.data.after;
        return {
          ...applyAction,
          data: newData
        };
      } else if (applyAction.operation === 'move') {
        store.setPage('addBlock', { ...applyAction.data, ...applyAction.data.after }, false);
        const newData = JSON.parse(JSON.stringify(applyAction.data));
        newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
        delete newData.before;
        delete newData.after;
        return {
          ...applyAction,
          sourceFlowUUID: applyAction.data.before.uuid,
          data: newData
        };
      } else if (applyAction.operation === 'delete') {
        store.setPage('deleteBlock', { ...applyAction.data, uuid: applyAction.data.flowUUID }, false);
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            deleteId: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][applyAction.data[applyAction.entity].uuid],
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
            }
          }
        };
      } else if (applyAction.operation === 'replace') {
        const changes = { ...applyAction.data, ...applyAction.data.after };
        store.setPage('replaceBlocks', { ...changes, uuid: applyAction.data.flowUUID }, false);
        return {
          entity: applyAction.entity,
          operation: 'replace',
          data: {
            flowUUID: applyAction.data.flowUUID,
            pageId: applyAction.data.pageId,
            uuid: applyAction.data.flowUUID,
            value: applyAction.data.after,
          },
        };
      } else if (applyAction.operation === 'update') {
        if (applyAction.data.updateKey === 'fontSize' || applyAction.data.updateKey === 'fontFamily') {
          const styleType = applyAction.data.updateKey
          const newFontFamilyOrSize = applyAction.data.after;
          const oldFontFamilyorSize = applyAction.data.before;
          store.updatefontFamilyAndSize(applyAction.entity, newFontFamilyOrSize, oldFontFamilyorSize, styleType, 'redo', false );
          return {
            entity: applyAction.entity,
            operation: 'update',
            data: {
              [applyAction.entity]: {
                uuid: applyAction.data[applyAction.entity].uuid,
              },
              updateKey: styleType,
              value: newFontFamilyOrSize,
            },
          };
        }
        const entityChanges = applyAction.data[applyAction.entity];
        store.updateBlock(
          entityChanges.key,
          entityChanges.uuid,
          {
            [entityChanges.key]: applyAction.data.after,
            flowUUID: applyAction.data.flowUUID,
            op: 'redo',
            callback: applyAction.data.callback,
            tableChanges: applyAction.data.tableChanges
          },
          false
        );
        const newData = JSON.parse(JSON.stringify(applyAction.data));
        newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
        delete newData.before;
        delete newData.after;
        newData.value = applyAction.data.after;
        return {
          ...applyAction,
          data: newData
        };
      }
    } else if (applyAction.entity === 'header' || applyAction.entity === 'footer') {
      if (applyAction.operation === 'add') {
        store.setPage('addStaticElement', { uuid: applyAction.data.flowUUID, type: applyAction.entity, block: applyAction.data[applyAction.entity] }, false);
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: { uuid: applyAction.data[applyAction.entity].uuid }
          }
        }
      } else if (applyAction.operation === 'delete') {
        store.setPage('deleteStaticElement', { uuid: applyAction.data.flowUUID, type: applyAction.entity }, false);
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
            }
          }
        }
      } else if (applyAction.operation === 'update') {
        const entityChanges = applyAction.data[applyAction.entity];
        store.updateBlock(
          entityChanges.key,
          entityChanges.uuid,
          {
            [entityChanges.key]: applyAction.data.after,
            flowUUID: applyAction.data.flowUUID,
            op: 'redo',
            callback: applyAction.data.callback,
            tableChanges: applyAction.data.tableChanges
          },
          false
        );
        const newData = JSON.parse(JSON.stringify(applyAction.data));
        newData[applyAction.entity] = { uuid: applyAction.data[applyAction.entity].uuid };
        delete newData.before;
        delete newData.after;
        newData.value = applyAction.data.after;
        return {
          ...applyAction,
          data: newData
        };
      }
    } else if (applyAction.entity === PAGE_NUMBER) {
      if (applyAction.operation === 'add') {
        store.addPageNumber(
          {
            uuid: applyAction.data.flowUUID,
            type: applyAction.entity,
            block: applyAction.data[applyAction.entity],
          },
          false,
        );
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: { uuid: applyAction.data[applyAction.entity].uuid },
          },
        };
      } else if (applyAction.operation === 'delete') {
        store.deletePageNumber(
          {
            uuid: applyAction.data.flowUUID,
            type: applyAction.entity,
            block: applyAction.data.pageNumber,
          },
          false,
        );
        return {
          ...applyAction,
          data: {
            ...applyAction.data,
            [applyAction.entity]: {
              uuid: applyAction.data[applyAction.entity].uuid,
            },
          },
        };
      }
     } else {
      // no-op
    }
  }, []);

  store.undo = useCallback(() => {
    historyStore.set({ action: 'undo', callback: store.applyReverseChanges});
  }, []);

  store.redo = useCallback(() => {
    historyStore.set({ action: 'redo', callback: store.applyChanges});
  }, []);

  store.clearDesign = useCallback(() => {
    store.set(initialContext());
    historyStore.set({ action: 'clearAll'});
  }, []);

  store.setComplexDocumentDesign = useCallback((existingDocument, updateOfflineStorage = true, currentDesign) => {
    const changesInStore = {
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: []
    };

    const count = {
      createBlocksCount: 0,
      chartElementsCount: 0
    };

    let flowBusinessLogicMap = new Map();
    let elementBusinessLogicMap = new Map();
    const flowBusinessLogicData = getBusinessLogicByType(existingDocument.businessLogics, 'flow');
    const elementBusinessLogicData = getBusinessLogicByType(existingDocument.businessLogics, 'element');
    const data = existingDocument.flows ? existingDocument.flows : existingDocument.flow ? [existingDocument.flow] : [existingDocument];
    const newDocumentFlows = data.map(existingFlow => {
      if (existingFlow.isDeleted) {
        return existingFlow;
      }
      let headerUUID, footerUUID, blocksUUID = [], blockMap = new Map();
      const isEmailTemplate = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]
      const newFlow = DEFAULT_NEW_FLOW(existingFlow, isEmailTemplate);
      changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].push(newFlow.uuid);
      changesInStore[newFlow.uuid] = newFlow;
      changesInStore[newFlow.uuid].isStandalone = existingFlow.isStandalone;
      const persistedKeys = {};
      const hasBusinessLogic = flowBusinessLogicData.find(flow => flow.rules[0]?.flowId === existingFlow.flowId);
      flowBusinessLogicMap.set(existingFlow.flowId, {blId: hasBusinessLogic?.id, isBLPresent: Boolean(hasBusinessLogic)});

      updateStoreForNewFlow(
        existingFlow,
        newFlow,
        persistedKeys,
        headerUUID,
        footerUUID,
        blocksUUID,
        blockMap,
        changesInStore,
        count,
        false,
        undefined,
        currentDesign,
        isEmailTemplate,
        elementBusinessLogicData,
        elementBusinessLogicMap
      );

      // Syncing header and footer
      /*if (existingFlow.header && !existingFlow.header.isDeleted) {
        const isStandaloneHeader = existingFlow.header.isStandalone;
        const existingFlowHeader = isStandaloneHeader
          ? existingFlow.header.blockDTOList[0]
          : existingFlow.header;
        const existingFlowHeaderContents = isStandaloneHeader
          ? existingFlow.header.blockDTOList[0].contents
          : existingFlow.header.contents;

        const headerBlock = Object.assign({}, existingFlowHeader, {
          blockId: existingFlow.header.blockId,
          uuid: uuidv4(),
          type: HEADER,
          contents: existingFlowHeaderContents.map(el => {
            if (el.contentType === ELEMENT_TYPES.CHART) {
              chartElementsCount += 1;
            } else if (el.contentType === STATIC_TABLE_ELEMENT) {
              return Object.assign({}, el, {
                uuid: uuidv4(),
                contents: el.contents.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
              });
            }
            return Object.assign({}, el, { uuid: uuidv4() });
          }),
        });
        isStandaloneHeader && delete headerBlock.blockDTOList;
        isStandaloneHeader && delete headerBlock.orderNumber;
        !isStandaloneHeader && delete headerBlock.blockId;

        changesInStore[newFlow.uuid].header = headerBlock.uuid;
        headerUUID = headerBlock.uuid;
        changesInStore[headerBlock.uuid] = headerBlock;
        if (existingFlow.header.blockId) {
          persistedKeys[headerBlock.uuid] = existingFlow.header.blockId;
        }
      }
      if (existingFlow.footer && !existingFlow.footer.isDeleted) {
        const isStandaloneFooter = existingFlow.footer.isStandalone;
        const existingFlowFooter = isStandaloneFooter
          ? existingFlow.footer.blockDTOList[0]
          : existingFlow.footer;
        const existingFlowFooterContents = isStandaloneFooter
          ? existingFlow.footer.blockDTOList[0].contents
          : existingFlow.footer.contents;

        const footerBlock = Object.assign({}, existingFlowFooter, {
          blockId: existingFlow.footer.blockId,
          uuid: uuidv4(),
          type: FOOTER,
          contents: existingFlowFooterContents.map(el => {
            if (el.contentType === ELEMENT_TYPES.CHART) {
              chartElementsCount += 1;
            } else if (el.contentType === STATIC_TABLE_ELEMENT) {
              return Object.assign({}, el, {
                uuid: uuidv4(),
                contents: el.contents.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
              });
            }
            return Object.assign({}, el, { uuid: uuidv4() });
          }),
        });
        isStandaloneFooter && delete footerBlock.blockDTOList;
        isStandaloneFooter && delete footerBlock.orderNumber;
        !isStandaloneFooter && delete footerBlock.blockId;
        
        changesInStore[newFlow.uuid].footer = footerBlock.uuid;
        footerUUID = footerBlock.uuid;
        changesInStore[footerBlock.uuid] = footerBlock;
        if (existingFlow.footer.blockId) {
          persistedKeys[footerBlock.uuid] = existingFlow.footer.blockId;
        }
      }
      if (existingFlow?.style?.pageNumber && Object.keys(existingFlow.style.pageNumber).length > 0) {
        const pageNumber = {
          ...existingFlow.style.pageNumber,
          uuid: uuidv4(),
          contents: { ...existingFlow.style.pageNumber.contents, contentType: 'text' },
        };
        changesInStore[newFlow.uuid].pageNumber = pageNumber.uuid;
        changesInStore[pageNumber.uuid] = pageNumber;
      }
      if (existingFlow.blocks.length > 0) {
        existingFlow.blocks.forEach(block => {
          if (block.isDeleted) {
            blocksUUID.push(block.uuid);
          } else {
          const newBlock = Object.assign({}, block, { uuid: uuidv4(), type: block.blockType });
          newBlock.blockType !== STANDALONE_BLOCK && delete newBlock.blockId;
          delete newBlock.orderNumber;
          if (newBlock.blockType === CREATIVE_BLOCK || newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
            newBlock.contents = newBlock.contents.map(el => {
              if (el.contentType === ELEMENT_TYPES.CHART) {
                chartElementsCount += 1;
              } else if (el.contentType === STATIC_TABLE_ELEMENT) {
                return Object.assign({}, el, {
                  uuid: uuidv4(),
                  contents: el.contents.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
                });
              }
              return Object.assign({}, el, { uuid: uuidv4() });
            });
            createBlocksCount++;
          }
          if (newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
            (newBlock.blockDTOList || []).forEach(childBlock => {
              if(!childBlock.uuid){
                childBlock.uuid = uuidv4();
              }
              const newChildBlock = Object.assign({}, childBlock, { type: childBlock.blockType });
              if (childBlock.blockId) {
                if (childBlock.blockId !== 'topBlock' || childBlock.blockId !== 'bottomBlock') {
                  persistedKeys[newChildBlock.uuid] = childBlock.blockId;
                }
              }
              newChildBlock.isChild = true;
              newChildBlock.parentBlockUUID = newBlock.uuid;
              blockMap.set(childBlock.blockId, newChildBlock.uuid);
              delete newChildBlock.blockId;
              delete newChildBlock.orderNumber;
              changesInStore[newChildBlock.uuid] = newChildBlock;
            });
            newBlock.style = Object.assign({}, newBlock.style, {
              tableProperties: Object.assign({}, newBlock.style.tableProperties, {
                topBlock: newBlock.style.tableProperties.topBlock ? blockMap.get(newBlock.style.tableProperties.topBlock) : undefined,
                bottomBlock: newBlock.style.tableProperties.bottomBlock ? blockMap.get(newBlock.style.tableProperties.bottomBlock) : undefined,
              })
            })
            delete newBlock.blockDTOList;
          }
          if (newBlock.blockType === STANDALONE_BLOCK) {
            newBlock.contents = (newBlock.blockDTOList || []).map((childBlock, index) => {
              const newChildBlockUUID = uuidv4();
              const new_childBlock = Object.assign({}, childBlock, {
                uuid: newChildBlockUUID,
                type: childBlock.blockType,
              });

              if (childBlock.blockId) {
                persistedKeys[new_childBlock.uuid] = childBlock.blockId;
              }
              if (
                new_childBlock.blockType === CREATIVE_BLOCK ||
                new_childBlock.blockType === DYNAMIC_TABLE_BLOCK
              ) {
                new_childBlock.contents = new_childBlock.contents.map(el => {
                  if (el.contentType === ELEMENT_TYPES.CHART) {
                    chartElementsCount += 1;
                  } else if (el.contentType === STATIC_TABLE_ELEMENT) {
                    return Object.assign({}, el, {
                      uuid: uuidv4(),
                      contents: el.contents.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
                    });
                  }
                  return Object.assign({}, el, { uuid: uuidv4(), type: el.contentType });
                });
              }

              if (new_childBlock.blockType === DYNAMIC_TABLE_BLOCK) {
                (new_childBlock.blockDTOList || []).forEach(childBlockObj => {
                  const tableChildBlock = Object.assign({}, childBlockObj, {
                    uuid: uuidv4(),
                    type: childBlockObj.blockType,
                  });
                  if (childBlockObj.blockId) {
                    if (
                      childBlockObj.blockId !== 'topBlock' ||
                      childBlockObj.blockId !== 'bottomBlock'
                    ) {
                      persistedKeys[tableChildBlock.uuid] = childBlockObj.blockId;
                    }
                  }
                  tableChildBlock.isChild = true;
                  tableChildBlock.parentBlockUUID = new_childBlock.uuid;
                  blockMap.set(childBlockObj.blockId, tableChildBlock.uuid);
                  delete tableChildBlock.blockId;
                  delete tableChildBlock.orderNumber;
                  changesInStore[tableChildBlock.uuid] = tableChildBlock;
                });
                new_childBlock.style = Object.assign({}, new_childBlock.style, {
                  tableProperties: Object.assign({}, new_childBlock.style.tableProperties, {
                    topBlock: new_childBlock.style.tableProperties.topBlock
                      ? blockMap.get(new_childBlock.style.tableProperties.topBlock)
                      : undefined,
                    bottomBlock: new_childBlock.style.tableProperties.bottomBlock
                      ? blockMap.get(new_childBlock.style.tableProperties.bottomBlock)
                      : undefined,
                  }),
                });
                delete new_childBlock.blockDTOList;
              }
              blocksUUID.push(new_childBlock.uuid);
              changesInStore[new_childBlock.uuid] = new_childBlock;
              return newChildBlockUUID;
            });
            delete newBlock.blockDTOList;
          }
          newFlow[`${newFlow.uuid}-0`].push(newBlock.uuid);
          blocksUUID.push(newBlock.uuid);
          changesInStore[newBlock.uuid] = newBlock;
          if (block.blockId) {
            persistedKeys[newBlock.uuid] = block.blockId;
          }
          }
        });
      }*/
      // TODO : Sync blocks within the flow as well
      changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS] = Object.assign({}, changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS], {
        [newFlow.uuid]: existingFlow.flowId,
        ...persistedKeys
      });
      return Object.assign({}, existingFlow, {
        uuid: newFlow.uuid,
        header: existingFlow.header ? Object.assign({}, existingFlow.header, {
          uuid: headerUUID,
        }) : undefined,
        footer: existingFlow.footer ? Object.assign({}, existingFlow.footer, {
          uuid: footerUUID,
        }) : undefined,
        blocks: existingFlow.blocks.map((existingBlock, index) => {
          return Object.assign({}, existingBlock, {
            uuid: blocksUUID[index],
            blockDTOList: existingBlock.blockDTOList?.map(childBlock => {
              return Object.assign({}, childBlock, {
                uuid: blockMap.get(childBlock.blockId)
              })
            })
          })
        })
      })
    });
    const designSummary = Object.assign({
      blockType: 'text',
      style: {},
      contents: undefined,
      variableContract: []
    }, existingDocument.summary);
    const designSummaryBlockId = designSummary?.blockId;
    if (designSummary) {
      delete designSummary.blockId;
    }
    updateOfflineStorage && putIndexedDBData('complexDocument', Object.assign({}, existingDocument, { flows: newDocumentFlows }));
    count.createBlocksCount += 1;
    store.set({
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.ID]: existingDocument.documentId || existingDocument.id || existingDocument.parentBlockId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]: existingDocument.name || existingDocument?.flow?.flowName || existingDocument.parentBlockName,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: existingDocument.style || {},
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID]: existingDocument.groupId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID]: existingDocument.newCommunicationId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.RESOURCE_GROUP_ID]: existingDocument.resourceGroup || existingDocument.resourceGroupId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.CATEGORY_ID]: existingDocument.categoryId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE]: existingDocument.language,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS]: existingDocument.tags ? existingDocument.tags.split(",") : [],
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT]: count.createBlocksCount,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]: count.chartElementsCount,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE]: existingDocument?.type || undefined,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.VARIABLE_OVERRIDES]: existingDocument?.dataSetOverride || [],
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY]: designSummary,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY_ID]: designSummaryBlockId,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: existingDocument?.inserts || [],
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EDITABLE]: existingDocument?.isEditable,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME]: existingDocument?.displayNameBlockDTO,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.RENDERING_TYPE]: existingDocument?.renderingType,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_ACCESSIBLE]: existingDocument?.accessibilityEnabled,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: flowBusinessLogicMap,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: elementBusinessLogicMap, 
      ...changesInStore
    });
    console.log("store.get :: ", store.get());
  }, [store]);


  const changeTOCElementUUIDs = useCallback((block) => {
    let duplicateBlock = block;
    duplicateBlock.contents.forEach((content, index) => {
      if (content?.style?.heading?.elementId) {
          duplicateBlock.contents[index].style.heading.elementId = uuidv4();
      }
    });
    return duplicateBlock;
  }, []);

  const changeCreativeBlockUUIDs = useCallback(block => {
    let duplicateBlock = block;
    duplicateBlock.contents.forEach((block, index) => {
      if (CREATIVE_BLOCK_ELEMENT_TYPES.includes(block.contentType)) {
        duplicateBlock.contents[index].uuid = uuidv4();
      }
      if(block.contentType === 'text') {
        block = changeTOCElementUUIDs(block);
      }
      if (block.contentType === 'static_table') {
        block.contents.forEach((cell, cellIndex) => {
          duplicateBlock.contents[index].contents[cellIndex].uuid = uuidv4();
        });
      }
    });
    return duplicateBlock;
  }, []);

  const changeGroupIds = useCallback(block => {
    let groupedIdsArr = [];
    let duplicateBlock = block;
    if (duplicateBlock?.groups && duplicateBlock.groups.length > 0) {
      duplicateBlock.groups.forEach((item => {
        const newGroupingId = uuidv4();
        groupedIdsArr.push({ existingGroupId: item.groupId, newGroupId: newGroupingId })
        item.groupId = newGroupingId;
        item.groupName = '';
      }))
    }
    const existingGroupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
    const tempMapGroup = new Map();
    duplicateBlock.contents.forEach((item => {   
      const newGroupId = groupedIdsArr.find(i => i.existingGroupId === item.groupId)?.newGroupId;
      item.groupId = newGroupId;
      tempMapGroup.set(newGroupId, {groupName: '', isBLPresent: false})
    }))
    const mergedGroup = new Map([...existingGroupMap, ...tempMapGroup]);
    store.set({[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: mergedGroup})
    return duplicateBlock;
  })

  store.duplicateFlow = useCallback(
    uuid => {
      const currentStore = store.get();
      const existingBlockUUIDs = currentStore[uuid][`${uuid}-0`];
      let newFlow = DEFAULT_NEW_FLOW(
        currentStore[uuid],
        store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
      );
      const persistedKeys = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS];
      if (currentStore[uuid].isStandalone) {
        persistedKeys[newFlow.uuid] =
          currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][uuid];
      }
      const newBlocks = {};
      const newBlockUUIDs = [];

      if (currentStore[uuid].pageNumber) {
        let duplicatePageNumber = currentStore[currentStore[uuid].pageNumber];
        duplicatePageNumber.uuid = uuidv4();
        newBlocks[duplicatePageNumber.uuid] = duplicatePageNumber;
        newFlow.pageNumber = duplicatePageNumber.uuid;
      }

      const duplicateExistingBlock = (type, block) => {
        let newBlock = deepCopy(currentStore[block]);
        if (!newBlock.isStandalone) {
          newBlock.uuid = uuidv4();
        }
        newBlock = changeCreativeBlockUUIDs(newBlock);
        newBlock = changeGroupIds(newBlock);
        newBlocks[newBlock.uuid] = newBlock;
        newFlow[type] = newBlock.uuid;
      };
      if (currentStore[uuid].header) {
        duplicateExistingBlock('header', currentStore[uuid].header);
      }
      if (currentStore[uuid].footer) {
        duplicateExistingBlock('footer', currentStore[uuid].footer);
      }

      existingBlockUUIDs.forEach(block => {
        let duplicateBlock = deepCopy(currentStore[block]);
        if (!duplicateBlock.isStandalone) {
          duplicateBlock.uuid = uuidv4();
        }
        if(duplicateBlock.blockType === 'text') {
          duplicateBlock = changeTOCElementUUIDs(duplicateBlock);
        }
        if (duplicateBlock.blockType === 'dynamic_table') {
          const duplicateTableBlock = (blockType, tableBlockUUID) => {
            let tableBlock = deepCopy(currentStore[tableBlockUUID]);
            const newUUID = uuidv4();
            duplicateBlock.style.tableProperties[blockType] = newUUID;
            tableBlock.uuid = newUUID;
            tableBlock.parentBlockUUID = duplicateBlock.uuid;
            const updatedBlock = changeCreativeBlockUUIDs(tableBlock);
            newBlocks[newUUID] = updatedBlock;
          };
          if (duplicateBlock.style.tableProperties.topBlock) {
            duplicateTableBlock('topBlock', duplicateBlock.style.tableProperties.topBlock);
          }
          if (duplicateBlock.style.tableProperties.bottomBlock) {
            duplicateTableBlock('bottomBlock', duplicateBlock.style.tableProperties.bottomBlock);
          }
          duplicateBlock.contents.forEach((cell, cellIndex) => {
            duplicateBlock.contents[cellIndex].uuid = uuidv4();
          });
        }
        if(duplicateBlock.blockType === 'static_table') {
          duplicateBlock.contents = duplicateBlock.contents.map(cell => {
            const blockUUID = uuidv4();
            if(store.get()[cell.uuid]) {
              let contentBlock = deepCopy(store.get()[cell.uuid]);
              contentBlock.uuid = blockUUID;
              if(contentBlock?.contentType === "text") {
                contentBlock = changeTOCElementUUIDs(contentBlock);
              }
              contentBlock.parentBlockUUID = duplicateBlock.uuid;
              newBlocks[blockUUID] = contentBlock;
            }
            return { ...cell, uuid: blockUUID };
          });
        }
        if (duplicateBlock.blockType === 'creative') {
          duplicateBlock = changeCreativeBlockUUIDs(duplicateBlock);
          duplicateBlock = changeGroupIds(duplicateBlock);
        }
        newBlocks[duplicateBlock.uuid] = duplicateBlock;
        newBlockUUIDs.push(duplicateBlock.uuid);
      });
      newFlow[`${newFlow.uuid}-0`] = newBlockUUIDs;
      const flowNumber = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].findIndex(flow => flow === uuid);
      const newFlows = [
        ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(0, flowNumber + 1),
        newFlow.uuid,
        ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(flowNumber + 1),
      ];
      const storeObj = {
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: newFlows,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS]: persistedKeys,
        ...newBlocks,
        [newFlow.uuid]: newFlow,
      };
      store.set(storeObj);
      historyStore.set({
        changes: { entity: 'flow', operation: 'add', data: { uuid: uuid, flow: newFlow } },
      });
      return newFlow.uuid;
    },
    [store],
  );

  store.setNewFlow = useCallback((flow, uuid,flowIndex ,isStandalone, currentDesign) => {
    console.log("setNewFlow called !!!");
    const changesInStore = {
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: []
    };
 
    let flowUuid; 
    const existingFlow  = flow;  

      if (existingFlow.isDeleted) {
        return existingFlow;
      }
      let headerUUID, footerUUID, blocksUUID = [];
      const count = {
        createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
        chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
      }
      const newFlow = DEFAULT_NEW_FLOW(existingFlow, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]);
      newFlow.isStandalone = isStandalone;
      changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].push(newFlow.uuid);
      changesInStore[newFlow.uuid] = newFlow;
      flowUuid = newFlow.uuid;
      const blockMap = new Map();
      const persistedKeys = {};
      const groupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
      const isCloned = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED];

      updateStoreForNewFlow(
        existingFlow,
        newFlow,
        persistedKeys,
        headerUUID,
        footerUUID,
        blocksUUID,
        blockMap,
        changesInStore,
        count,
        !isStandalone,
        undefined,
        currentDesign,
        false,
        [],
        groupMap,
        isCloned

      );
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]: false })
      // Syncing header and footer
      /* if (existingFlow.header && !existingFlow.header.isDeleted) {
        const headerBlock = Object.assign({}, existingFlow.header, {
          uuid: uuidv4(),
          type: HEADER,
          contents: existingFlow.header.contents.map(el =>
            Object.assign({}, el, { uuid: uuidv4() }),
          ),
        });
        delete headerBlock.blockId;
        changesInStore[newFlow.uuid].header = headerBlock.uuid;
        headerUUID = headerBlock.uuid;
        changesInStore[headerBlock.uuid] = headerBlock;
        if (existingFlow.header.blockId) {
          persistedKeys[headerBlock.uuid] = existingFlow.header.blockId;
        }
      }
      if (existingFlow.footer && !existingFlow.footer.isDeleted) {
        const footerBlock = Object.assign({}, existingFlow.footer, {
          uuid: uuidv4(),
          type: FOOTER,
          contents: existingFlow.footer.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
        });
        delete footerBlock.blockId;
        changesInStore[newFlow.uuid].footer = footerBlock.uuid;
        footerUUID = footerBlock.uuid;
        changesInStore[footerBlock.uuid] = footerBlock;
        if (existingFlow.footer.blockId) {
          persistedKeys[footerBlock.uuid] = existingFlow.footer.blockId;
        }
      }
      if (existingFlow?.blocks && existingFlow?.blocks?.length > 0) {
        existingFlow.blocks.forEach(block => {
          if (block.isDeleted) {
            blocksUUID.push(block.uuid);
          } else {
            const newBlock = Object.assign({}, block, { uuid: uuidv4(), type: block.blockType });
            delete newBlock.blockId;
            delete newBlock.orderNumber;
            if (newBlock.blockType === CREATIVE_BLOCK || newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
              newBlock.contents = newBlock.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
            }
            if (newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
              console.log({ newBlock, block });
            }
            newFlow[`${newFlow.uuid}-0`].push(newBlock.uuid);
            blocksUUID.push(newBlock.uuid);
            changesInStore[newBlock.uuid] = newBlock;
            if (block.blockId) {
              persistedKeys[newBlock.uuid] = block.blockId;
            }
          }
        });
      } */
      const currentStore = store.get(); 

      // copy and add page number to the reference/reusable flow
      const prevFlowIndex = flowIndex === 0 ? 0 : flowIndex - 1;
      const prevFlowUUID = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][prevFlowIndex];
      const prevFlow = currentStore[prevFlowUUID];
      /*if (prevFlow && prevFlow?.pageNumber) {
        const pageNumber = { ...currentStore[prevFlow?.pageNumber], uuid: uuidv4() };
        changesInStore[newFlow.uuid].pageNumber = pageNumber.uuid;
        changesInStore[pageNumber.uuid] = pageNumber;
      }*/
    // if (isStandalone) {
      changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS] = {
        ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS],
        ...persistedKeys,
        [newFlow.uuid]: isStandalone ? existingFlow.flowId : undefined
      }
      // changesInStore[newFlow.uuid].isStandalone = true;
    // };

    const newFlowsId = [...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(0, flowIndex ),
      flowUuid,
    ...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].slice(flowIndex )
    ];  

    const findIndex = newFlowsId.findIndex((id)=> id === flowUuid);
    
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]= newFlowsId;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT] = count.createBlocksCount;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT] = count.chartElementsCount;

    console.log("Changes in store :: ", changesInStore);
   
    store.set({
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.REFRENCED_FLOW_UUID]: [...currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.REFRENCED_FLOW_UUID], flowUuid],
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: newFlowsId,
      ...changesInStore,
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_FLOW]: undefined
    });

    historyStore.set({ changes: { entity: 'flow', operation: 'add', data: { uuid: newFlowsId[findIndex === 0 ? flowIndex : findIndex - 1], flow: store.get()[flowUuid] } } });   
    return flowUuid;
    
  }, [store]);

  store.replaceFlow = useCallback((details, userAction= true) => {
    console.log("replaceFlow called !!!");
    const changesInStore = {
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: []
    };

    if(details.flow.flowName === TABLE_OF_CONTENTS){
      details.flow.uuid = details.uuid
    }

    const copyOfPrevFlows = deepCopy(store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]);
    const droppedFlow = details.flow;
    const newFlow = DEFAULT_NEW_FLOW(details.flow, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]);
    
    let headerUUID, footerUUID, blocksUUID = [];
    let persistedKeys = {};

    changesInStore[newFlow.uuid] = newFlow;

/*    if (droppedFlow?.header && !droppedFlow?.header.isDeleted) {
      const headerBlock = Object.assign({}, droppedFlow.header, {
        uuid: uuidv4(),
        type: HEADER,
        contents: droppedFlow.header.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
      });
      delete headerBlock.blockId;
      changesInStore[newFlow.uuid].header = headerBlock.uuid;
      changesInStore[headerBlock.uuid] = headerBlock;
    }

    if (droppedFlow?.footer && !droppedFlow?.footer.isDeleted) {
      const footerBlock = Object.assign({}, droppedFlow.footer, {
        uuid: uuidv4(),
        type: FOOTER,
        contents: droppedFlow.footer.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
      });
      delete footerBlock.blockId;
      changesInStore[newFlow.uuid].footer = footerBlock.uuid;
      changesInStore[footerBlock.uuid] = footerBlock;
    }

    if (droppedFlow?.blocks && droppedFlow?.blocks?.length > 0) {
      droppedFlow.blocks.forEach(block => {
        if (!block.isDeleted) {
          const newBlock = Object.assign({}, block, { uuid: uuidv4(), type: block.blockType });
          delete newBlock.blockId;
          if (newBlock.blockType === CREATIVE_BLOCK || newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
            newBlock.contents = newBlock.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
          }
          blockUUIDs.push(newBlock.uuid);
          changesInStore[newBlock.uuid] = newBlock;
        }
      });
    }*/
    let count = {
      createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
      chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
    };
    const blockMap = new Map();
    const existingFlow = deepCopy(details.flow);

    updateStoreForNewFlow(
      existingFlow,
      newFlow,
      persistedKeys,
      headerUUID,
      footerUUID,
      blocksUUID,
      blockMap,
      changesInStore,
      count,
      !droppedFlow.isStandalone
    );

    const oldFlowUUID = details.uuid;
    // console.log({ persistedKeys });

    const currentStore = deepCopy(store.get());
    let flowUUIDs = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
    const index = flowUUIDs.indexOf(oldFlowUUID);
    flowUUIDs[index] = newFlow.uuid;
   
    changesInStore[newFlow.uuid][`${newFlow.uuid}-0`] = blocksUUID;
    changesInStore[newFlow.uuid] =  newFlow
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT] = count.createBlocksCount;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT] = count.chartElementsCount;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS] = flowUUIDs;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS] = Object.assign({}, currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS], {
      ...persistedKeys
    });

    store.set({
      ...changesInStore, 
    },[]);

    if(userAction){
      historyStore.set({
        changes: { entity: 'flow', operation: 'replace', data: {
          flow: { uuid: newFlow.uuid },
          deleteId: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][oldFlowUUID],
          before: copyOfPrevFlows,
          after: flowUUIDs,
          oldFlowUUID
        }}
      });
    }
  }, [store]);

  store.replaceDocument = useCallback((details, documentStyle, callback) => {
    const changesInStore = {
      [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS]: []
    };

    if (documentStyle) {
      changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES] = documentStyle;
    }

    const existingFlows = details;
    let flowUUIDs = [];
    let copyOfFlows;
    const currentStore = store.get();
    const copyOfPrevFlowIds = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS];
    let persistedKeys = {};
    const blockIdsMap = new Map();
    const flowIdsMap = new Map();

    let count = {
      createBlocksCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT],
      chartElementsCount: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT]
    }

    existingFlows.map((existingFlow) => {
      let headerUUID, footerUUID, blocksUUID = [];
      const blockMap = new Map();
      const newFlow = DEFAULT_NEW_FLOW(existingFlow, store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]);
      flowIdsMap.set(existingFlow.uuid, newFlow.uuid);

      updateStoreForNewFlow(
        existingFlow,
        newFlow,
        persistedKeys,
        headerUUID,
        footerUUID,
        blocksUUID,
        blockMap,
        changesInStore,
        count,
        !newFlow.isStandalone,
        blockIdsMap
      );

    changesInStore[newFlow.uuid][`${newFlow.uuid}-0`] = blocksUUID;
    changesInStore[newFlow.uuid] =  newFlow;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CREATE_BLOCK_COUNT] = count.createBlocksCount;
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.CHART_ELEMENTS_COUNT] = count.chartElementsCount;
      /* if (existingFlow.header && !existingFlow.header.isDeleted) {
        const headerBlock = Object.assign({}, existingFlow.header, {
          uuid: uuidv4(),
          type: HEADER,
          contents: existingFlow.header.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
        });
        delete headerBlock.blockId;
        changesInStore[newFlow.uuid].header = headerBlock.uuid;
        changesInStore[headerBlock.uuid] = headerBlock;
      }
      if (existingFlow.footer && !existingFlow.footer.isDeleted) {
        const footerBlock = Object.assign({}, existingFlow.footer, {
          uuid: uuidv4(),
          type: FOOTER,
          contents: existingFlow.footer.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
        });
        delete footerBlock.blockId;
        changesInStore[newFlow.uuid].footer = footerBlock.uuid;
        changesInStore[footerBlock.uuid] = footerBlock;
      }
      if (existingFlow?.blocks && existingFlow?.blocks?.length > 0) {
        existingFlow.blocks.forEach(block => {
          if (!block.isDeleted) {
            const newBlock = Object.assign({}, block, { uuid: uuidv4(), type: block.blockType });
            delete newBlock.blockId;
            delete newBlock.orderNumber;
            if (newBlock.blockType === CREATIVE_BLOCK || newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
              newBlock.contents = newBlock.contents.map(el => Object.assign({}, el, { uuid: uuidv4() }))
            }

            if(newBlock.blockType === DYNAMIC_TABLE_BLOCK){
              let blockMap = new Map();
              (newBlock.blockDTOList || []).forEach(childBlock => {
                const newUUID = uuidv4();
                const oldUUID = childBlock.uuid;

                const newChildBlock = Object.assign({}, childBlock, { uuid: newUUID, type: childBlock.blockType });
                if (childBlock.blockId) {
                  if (childBlock.blockId !== 'topBlock' || childBlock.blockId !== 'bottomBlock') {
                    persistedKeys[newChildBlock.uuid] = childBlock.blockId;
                  }
                }
                newChildBlock.isChild = true;
                newChildBlock.parentBlockUUID = newBlock.uuid;
                blockMap.set(oldUUID, newChildBlock.uuid);
                delete newChildBlock.blockId;
                delete newChildBlock.orderNumber;
                changesInStore[newChildBlock.uuid] = newChildBlock;
              });
              newBlock.style = Object.assign({}, newBlock.style, {
                tableProperties: Object.assign({}, newBlock.style.tableProperties, {
                  topBlock: newBlock.style.tableProperties.topBlock ? blockMap.get(newBlock.style.tableProperties.topBlock) : undefined,
                  bottomBlock: newBlock.style.tableProperties.bottomBlock ? blockMap.get(newBlock.style.tableProperties.bottomBlock) : undefined,
                })
              })
              delete newBlock.blockDTOList;
            }

            newFlow[`${newFlow.uuid}-0`].push(newBlock.uuid);
            blocksUUID.push(newBlock.uuid);
            changesInStore[newBlock.uuid] = newBlock;
          }
        });
      }
      if (existingFlow?.style?.pageNumber) {
        const pageNumber = {...existingFlow.style.pageNumber, uuid: uuidv4()};
        changesInStore[newFlow.uuid].pageNumber = pageNumber.uuid;
        changesInStore[pageNumber.uuid] = pageNumber; 
      } */

      flowUUIDs.push(newFlow.uuid);
    });

    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS] = flowUUIDs;
    let backendIds = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS];
    changesInStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS] = Object.assign({}, backendIds, {
      ...persistedKeys
    });
    callback && callback({blockIdsMap, flowIdsMap});
    store.set({
      ...changesInStore, 
    },[]);
    historyStore.set({ changes: { entity: 'flow', operation: 'replace', data: { flow: { uuid: flowUUIDs }, 
    deleteId: copyOfPrevFlowIds.map(key => backendIds[key]), 
    before: copyOfPrevFlowIds, after: flowUUIDs, oldFlowUUID: copyOfPrevFlowIds } }})
    return blockIdsMap;
  }, [store])

  store.fetchAllVariables = useCallback(async () => {
    try {
      const response = await getAllVariables();
      if (response && response.errors?.length === 0) {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]: response.data.map(variable => ({
            id: variable.id,
            variableCategoryId: variable.variableCategoryId,
            variableCategoryName: variable.variableCategoryName,
            name: variable.name,
            placeHolderText: variable.placeHolderText,
            exampleValue: variable.exampleValue,
            type: variable.type,
            length: variable.length,
            count: variable.count,
            mandatory: variable.mandatory,
            checkLength: variable.checkLength,
            displayName: variable.displayName || null
          })),
        });
      } else {
        // TODO : Check if any warning is needed in this case
      }
    } finally {
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: false });
    }
  }, []);

  store.fetchAllVariablesRelations = useCallback(async () => {
    try {
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: true });
      const response = await getAllVariableRelations();
      if (response && response?.data && response.errors?.length === 0) {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.RELATIONSHIPS]: response.data.map(relation => ({
            id: relation.id,
            variableCategoryId: 'relation',
            variableCategoryName: 'Relationship',
            name: relation.targetCategoryName,
            placeHolderText: `${relation.sourceCategoryName}.${relation.targetCategoryName}`,
            type: PERFECTDOC_RELATIONSHIP,
            isList: relation.isArray,
          })),
        });
      } else {
        
      }
    } finally {
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: false });
    }
  }, []);

  store.addNewVariable = useCallback(async (variable, local = false) => {
    if(variable) {
      if (local) {
        const newList = [variable, ...store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES]];
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.LOCAL_VARIABLES]: newList
        });
        return [variable, newList];
      }
      let response = await saveVariable(variable);
      if (response && response.errors && response.errors.length === 0) {
        const newVar = Object.assign({}, variable, { id: response.id });
        const newList = [newVar, ...store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]];
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]: newList
        })
        return [newVar, newList];
      } else {

      }
    }
  }, []);

  store.createNewFlow = useCallback(async (uuid) => { // TODO : Remove this usage, currently standalone blocks and dynamic tables with child blocks are not persisted
    
    let currentStore =  JSON.parse(JSON.stringify(store.get()));
/*    const contextFlow = currentStore[uuid];
    const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
      return [...acc, ...contextFlow[pageID]];
    }, []);

    let header = undefined;
    if (contextFlow?.header) {
      header = currentStore[contextFlow.header];
      delete header.type;
    }

    let footer = undefined;
    if (contextFlow?.footer) {
      footer = currentStore[contextFlow.footer];
      delete footer.type;
    }

    const newFlow = {
      uuid:uuid,
      style: {
        padding: {
          top: contextFlow.margin[0],
          right: contextFlow.margin[1],
          bottom: contextFlow.margin[2],
          left: contextFlow.margin[3]
        },
        width: contextFlow.width,
        height: contextFlow.height,
        backgroundColor: contextFlow.backgroundColor,
        pageNumber: contextFlow.pageNumber ? { ...contextFlow[contextFlow.pageNumber] } : undefined
      },
      paperSize: contextFlow.size,
      paperOrientation: contextFlow.orientation,
      header: header,
      footer: footer,
      blocks: allBlocks.reduce((acc, pageID, index) => {
        const block = { ...currentStore[pageID], orderNumber: index + 1 };
        delete block.type;
        return [...acc, block];
      }, [])
    };*/

    const { documentDTO } = generatePDFPayload(
      store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
      currentStore,
      [],
      documentStyle,
      'document',
      uuid,
      true
    );

    const newFlow = documentDTO.flows[0];
    newFlow.uuid = uuid; // UUID is carried to the payload formed for attachment

    console.log("newFlow :: ", newFlow);

    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]: newFlow }) // TODO : Change name of context key
  }, []);

  store.createFlowObject = useCallback((uuid) => { // TODO : Remove this usage
    const currentStore =  JSON.parse(JSON.stringify(store.get()));
    const contextFlow = currentStore[uuid];  
    const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
      return [...acc, ...contextFlow[pageID]];
    }, []);

    let header = undefined;
    if (contextFlow?.header) {
      header = currentStore[contextFlow.header];
      delete header.type;
    }

    let footer = undefined;
    if (contextFlow?.footer) {
      footer = currentStore[contextFlow.footer];
      delete footer.type;
    }

    const newFlowObj = {      
      style: {
        padding: {
          top: contextFlow.margin[0],
          right: contextFlow.margin[1],
          bottom: contextFlow.margin[2],
          left: contextFlow.margin[3]
        },
        width: contextFlow.width,
        height: contextFlow.height,
        backgroundColor: contextFlow.backgroundColor,
        pageNumber: contextFlow.pageNumber ? { ...contextFlow[contextFlow.pageNumber] } : undefined
      },
      paperSize: contextFlow.size,
      paperOrientation: contextFlow.orientation,
      header: header,
      footer: footer,
      blocks: allBlocks.reduce((acc, pageID) => {
        const block = currentStore[pageID];
        delete block.type;
        return [...acc, block];
      }, [])
    };  
    return newFlowObj;
  }, []);

  store.createDocumentPayload = useCallback(async (flowId, flowUUID) => { // TODO : Why is there no undo redo in this change?????????
    const complexDocument = await getIndexedDBData('complexDocument');
    const currentStore = JSON.parse(JSON.stringify(store.get()));

    const removeFlow = {
      flowId: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][flowUUID],
      isStandalone: false,
      isDeleted: true,
    }

    complexDocument.flows = complexDocument.flows.map((item) => {
      if (item.uuid === flowUUID) {
        item.flowId = flowId;
        item.isStandalone = true;

        return item;
      }
      return item;
    })

    complexDocument.flows = [...complexDocument.flows, removeFlow];

      currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][flowUUID] = flowId;
      currentStore[flowUUID].isStandalone = true;
      store.set({
        ...currentStore,
      }, []);

    return complexDocument;
  }, []);

  const fetchVariablesAndRelations = async () => {
    await store.fetchAllVariables();
    await store.fetchAllVariablesRelations();
  }
  

  useEffect(() => {
    // pass master variables to context and set the global variables with master variables
    // for displaying master templates with proper variables in template preview.
    if (masterVariables?.length) {
      store.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES]: masterVariables,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: false,
      });
    } else if (isOpenDesign) {
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES]: false });
    } else {
      fetchVariablesAndRelations();
    }
  }, [masterVariables]);

  return (
    <ComplexDocumentContext.Provider value={store}>
      {children}
    </ComplexDocumentContext.Provider>
  )
}

const TranslatedProvider = withTranslation(['COMPLEX_PAGE_OPTIONS', 'DYNAMIC_TABLE_OPERATIONS'])(Provider);

const ComplexDocumentHistoryProvider = ({ children, dataSet = undefined, masterVariables, isOpenDesign, customState }) => {

  // TODO : Implementation for debounce save
  const customStoreSetter = useCallback((currentStore, { action = '', changes, isUndoRedoAction = false, callback }) => {   
    // if (currentStore.applyingChanges) return currentStore;
    if (action === 'undo') {
      if (currentStore.undoStack.length > 0) {
        const action = currentStore.undoStack[currentStore.undoStack.length - 1];
        console.log("undo action :: ", action);
        let reverseAction;
        if (callback) {
          reverseAction = callback(action);
        }
        currentStore.undoStack = currentStore.undoStack.slice(0, currentStore.undoStack.length - 1); // remove item from undostack
        currentStore.redoStack = [...currentStore.redoStack, action]; // add item into redo stack
        if (reverseAction) {
          currentStore.history = [...currentStore.history, reverseAction];
        }
        currentStore.refreshStack = true;
      } else {
        // no-op
      }
    } else if (action === 'redo') {
      if (currentStore.redoStack.length > 0) {
        const action = currentStore.redoStack[currentStore.redoStack.length - 1];
        console.log("redo action :: ", action);
        let redoAction;
        if (callback) {
          redoAction = callback(action);
        }
        currentStore.redoStack = currentStore.redoStack.slice(0, currentStore.redoStack.length - 1); // remove item from redostack
        currentStore.undoStack = [...currentStore.undoStack, action]; // add item into undo stack
        if (redoAction) {
          currentStore.history = [...currentStore.history, redoAction];
        }
        currentStore.refreshStack = true;
      } else {
        // no-op
      }
    } else if (action === 'clear') {
      console.log("history action clear!!");
      if (changes.count && changes.count !== 0) {
        currentStore.history = currentStore.history.slice(changes.count);
      } else {
        currentStore.history = [];
      }
    }  else if (action === 'clearAll') {
      console.log("history action clearAll!!");
      currentStore = HISTORY_INITIAL_STATE();
    } else {
      console.log("history action :: ", changes);
      const historyChange = JSON.parse(JSON.stringify(changes));
      if (changes.data?.before !== undefined && changes.data?.after !== undefined) {
        historyChange.data.value = changes.data.after;
        delete historyChange.data.before;
        delete historyChange.data.after;
      }
      currentStore.history = [...currentStore.history, historyChange];
      let undoChanges = [changes]
      if (changes.undoLength !== undefined) {
        undoChanges = (new Array(changes.undoLength)).fill(0).map(() => changes);
      }
      currentStore.undoStack = [...currentStore.undoStack, ...undoChanges];
      if (changes.undoLength === 0 && changes.textOperation !== undefined && changes.textOperation === 'record') { // Case where nothing is added as undoChanges but the last undo stack has to be modified
        if (currentStore.undoStack.length > 0) {
          let lastChange = currentStore.undoStack[currentStore.undoStack.length - 1];
          if (lastChange.data.after && lastChange.data.after.uuid === changes.data.after.uuid) {
            lastChange.data.after =  changes.data.after;
          }
          currentStore.undoStack = currentStore.undoStack.toSpliced(currentStore.undoStack.length - 1, 1, lastChange);
        }
      }
      console.log("currentStore.undoStack :: ", currentStore.undoStack, changes.undoLength, changes.textOperation);
      if (changes.redoLength !== undefined) {
        currentStore.redoStack = currentStore.redoStack.slice(0, currentStore.redoStack.length - changes.redoLength);
        console.log("currentStore.redoStack :: ", currentStore.redoStack, changes.redoLength);
      }
      if (changes.textOperation !== undefined && changes.textOperation !== 'record') {
        // TODO : Not allow text changes to reset the redo stack
      } else if (currentStore.refreshStack && (currentStore.undoStack.length > 0 || currentStore.redoStack.length > 0)) {
        console.log("redo reset action !! ", currentStore.refreshStack, currentStore.undoStack.length, currentStore.redoStack.length);
        currentStore.redoStack = [];
        currentStore.refreshStack = false;
      }
    }
    return currentStore;
  }, []);

  const store = useCreateSubscriptionContext(HISTORY_INITIAL_STATE(), customStoreSetter, false);

  store.mergeChanges = useCallback((history = []) => {
    if (history.length === 0) return;
    // TODO : merge the history changes
    let changeInEntities = [];
    history.forEach(change => {
      try {
      // const entityUUID = change.data[change.entity].uuid;
      /*if (change.operation === 'replace' && typeof entityUUID !== 'string') {
        entityUUID = entityUUID[0];
      }*/ // TODO: check how merge changes work for array keys
      const isEntityAlreadyAdded = changeInEntities.findIndex(flowChange => flowChange.uuid === change.data[change.entity].uuid);
      console.log({ isEntityAlreadyAdded });
      if (isEntityAlreadyAdded === -1) { // This is the first change in the entity
        if(change.entity ==='document'){
          changeInEntities.push(Object.assign({}, change.data, {
            op: change.operation,
            entity: change.entity,
          }));
        }else{
          changeInEntities.push(Object.assign({}, change.data, {
            op: change.operation,
            entity: change.entity,
            uuid: change.data[change.entity]?.uuid || change.data.uuid
          }));
        }
      } else {
        if (change.operation === 'add') {
          if (changeInEntities[isEntityAlreadyAdded].op === 'delete') {
            changeInEntities = changeInEntities.filter(flowChange => flowChange.uuid !== change.data[change.entity].uuid);
          }
        } else if (change.operation === 'update' || change.operation === 'move') {
          changeInEntities = changeInEntities.map((flowChange, index) => {
            if (index === isEntityAlreadyAdded) {
              return Object.assign({}, flowChange, change.data, {
                uuid: flowChange.uuid,
                op: flowChange.op === 'delete' ? change.operation : flowChange.op // TODO : Update or move cannot happen immediately after delete, hence check if this is needed
              });
            }
            return flowChange;
          })
        } else if (change.operation === 'delete') {
          if (change.data.deleteId) {
            changeInEntities = changeInEntities.map((flowChange, index) => {
              if (index === isEntityAlreadyAdded) {
                return Object.assign({}, flowChange, change.data, { uuid: flowChange.uuid, op: 'delete' });
              }
              return flowChange;
            })
          } else {
            changeInEntities = changeInEntities.filter(flowChange => flowChange.uuid !== change.data[change.entity].uuid);
          }
        }
      }
      } catch (err) {
        console.error("Error in merge changes for loop :: ", err);
      }
    });
    console.log({ changeInEntities });
    return changeInEntities;
    // TODO : convert the changes to a payload according to the contract
  }, []);

  return (
    <ComplexDocumentHistoryContext.Provider value={store}>
      <ComplexDefaultStyleProvider>
        <TranslatedProvider dataSet={dataSet} masterVariables={masterVariables} isOpenDesign={isOpenDesign} customState={customState}>
          {children}
        </TranslatedProvider>
      </ComplexDefaultStyleProvider>
    </ComplexDocumentHistoryContext.Provider>
  )
}

export default ComplexDocumentHistoryProvider;
